import { useState, useEffect } from 'react';
import { API, downloadAuthed, openAuthed } from '@/lib/axios';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { HardHat, LogOut, Users, BookOpen, Award, TrendingUp, AlertTriangle, Search, Plus, GraduationCap, UserPlus, CheckCircle, CheckCircle2, Download, FileText, Eye, ToggleLeft, ToggleRight, MessageSquare, Mail, Bell, ClipboardList, Upload, FileUp, Trash2, CloudUpload, BarChart, Beaker, PieChart, Activity, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
// Billing removed - going different route
import IncidentReportSettings from '../components/IncidentReportSettings';
import PendingTransfers from '../components/PendingTransfers';
import NotificationBell from '@/components/NotificationBell';
import NearMissDashboard from '@/components/NearMissDashboard';
import CompanyTrainingManager from '@/components/CompanyTrainingManager';

// Check if running in native mobile app (iOS/Android) - hide pricing per App Store guidelines
const isNativeApp = (() => {
  try {
    if (window.Capacitor?.isNativePlatform?.()) return true;
    const protocol = window.location.protocol;
    if (protocol === 'capacitor:' || protocol === 'ionic:' || protocol === 'file:') return true;
    const hostname = window.location.hostname;
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && !window.location.port) return true;
    return false;
  } catch (e) {
    return false;
  }
})();

const AdminDashboard = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [courses, setCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]); // All courses for admin configuration
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequiredCourses, setSelectedRequiredCourses] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const [showBillingDialog, setShowBillingDialog] = useState(false);
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  const [licenseCount, setLicenseCount] = useState('');
  const [calculatedPricing, setCalculatedPricing] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  
  // Forms management state
  const [forms, setForms] = useState([]);
  const [showCreateFormDialog, setShowCreateFormDialog] = useState(false);
  const [showFormSubmissionsDialog, setShowFormSubmissionsDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]); // For super admin
  
  // PDF Forms management state
  const [pdfForms, setPdfForms] = useState([]);
  const [showCreatePdfFormDialog, setShowCreatePdfFormDialog] = useState(false);
  const [showPdfSubmissionsDialog, setShowPdfSubmissionsDialog] = useState(false);
  const [selectedPdfForm, setSelectedPdfForm] = useState(null);
  const [pdfSubmissions, setPdfSubmissions] = useState([]);
  const [pdfFormData, setPdfFormData] = useState({
    title: '',
    description: '',
    companyId: ''
  });
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  
  // Worker ID uploads state
  // Worker ID state removed - now accessible via File Manager
  
  // Messaging state
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageForm, setMessageForm] = useState({
    subject: '',
    message: '',
    recipients: 'all' // 'all', 'incomplete', 'specific'
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Training Reminder state
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    recipientType: 'all', // 'all', 'not_started', 'incomplete', 'specific'
    messageType: 'general', // 'general', 'not_started', 'incomplete', 'custom'
    customMessage: '',
    userIds: []
  });
  const [sendingReminders, setSendingReminders] = useState(false);

  // Toolbox Talks state
  const [toolboxTalks, setToolboxTalks] = useState([]);
  const [showCreateTalkDialog, setShowCreateTalkDialog] = useState(false);
  const [showUploadTalkDialog, setShowUploadTalkDialog] = useState(false);
  const [showViewTalkDialog, setShowViewTalkDialog] = useState(false);
  const [selectedTalk, setSelectedTalk] = useState(null);
  const [talkForm, setTalkForm] = useState({
    title: '',
    description: '',
    content: '',
    category: ''
  });
  const [selectedTalkPdf, setSelectedTalkPdf] = useState(null);
  const [uploadingTalk, setUploadingTalk] = useState(false);
  const [toolboxSessions, setToolboxSessions] = useState([]);
  const [showToolboxSessionsDialog, setShowToolboxSessionsDialog] = useState(false);


  // Incident Reports state
  const [incidentReports, setIncidentReports] = useState([]);
  const [showIncidentReportsDialog, setShowIncidentReportsDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [incidentFilter, setIncidentFilter] = useState('all'); // all, draft, submitted, under_review, resolved
  const [updatingIncidentStatus, setUpdatingIncidentStatus] = useState(false);

  // Submitted JSAs state
  const [submittedJsas, setSubmittedJsas] = useState([]);
  const [showJsaDialog, setShowJsaDialog] = useState(false);
  const [selectedJsa, setSelectedJsa] = useState(null);
  const [loadingJsas, setLoadingJsas] = useState(false);

  // Cloud Storage state
  const [cloudStorageConfig, setCloudStorageConfig] = useState(null);
  const [showCloudStorageDialog, setShowCloudStorageDialog] = useState(false);
  const [cloudStorageForm, setCloudStorageForm] = useState({
    provider: 'aws_s3',
    accessKey: '',
    secretKey: '',
    bucketName: '',
    region: '',
    customEndpoint: ''
  });
  const [savingCloudStorage, setSavingCloudStorage] = useState(false);

  // File Manager state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showFilesDialog, setShowFilesDialog] = useState(false);

  // Tax Rate state
  // Tax rate removed - going different route

  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fields: [],
    companyId: ''
  });
  const [newField, setNewField] = useState({
    label: '',
    type: 'text',
    required: false,
    options: []
  });
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferWorker, setTransferWorker] = useState(null);
  const [destinationCompanyCode, setDestinationCompanyCode] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  
  // Edit User States
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: '',
    isActive: true
  });
  
  // Create User States
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'worker',
    phone: '',
    craft: ''
  });
  const [crafts, setCrafts] = useState([]);
  
  // Classroom Learning States
  const [showStudentDialog, setShowStudentDialog] = useState(false);
  const [showPassDialog, setShowPassDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [studentForm, setStudentForm] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    password: '',
    craft: ''
  });
  const [classroomStudents, setClassroomStudents] = useState([]);
  const [showAddExistingUserDialog, setShowAddExistingUserDialog] = useState(false);
  const [selectedExistingUser, setSelectedExistingUser] = useState(null);

  useEffect(() => {
    loadData();
    loadBillingInfo();
    loadForms();
    loadPdfForms();
    // loadWorkerIds() removed - Worker IDs accessible via File Manager
    loadToolboxTalks(); // Phase 1: Re-enabled data loading
    loadCloudStorageConfig(); // Load cloud storage configuration
    loadIncidentReports(); // Load incident reports
    loadSubmittedJsas(); // Load submitted JSAs
    if (user.role === 'super_admin') {
      loadCompaniesForSuperAdmin();
    }
  }, [user.role]);

  const loadData = async () => {
    try {
      const [analyticsRes, coursesRes, allCoursesRes, usersRes, craftsRes] = await Promise.all([
        axiosInstance.get('/admin/analytics'),
        axiosInstance.get('/courses'),
        axiosInstance.get('/admin/all-courses'),
        axiosInstance.get('/admin/users'),
        axiosInstance.get('/crafts')
      ]);
      
      console.log('Loaded courses:', coursesRes.data.length, 'courses');
      console.log('Loaded ALL courses:', allCoursesRes.data.length, 'courses');
      
      setAnalytics(analyticsRes.data);
      setCourses(coursesRes.data);
      setAllCourses(allCoursesRes.data);
      setUsers(usersRes.data);
      setCrafts(craftsRes.data);
      
      // Tax rate removed
    } catch (error) {
      console.error('Failed to load data:', error);
      console.error('Error details:', error.response?.data);
      
      // Check if it's a permission error
      if (error.response?.status === 403) {
        toast.error(t('adminToasts.AccessDeniedYouNeedAdminPermissionsToViewThisPage'));
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load data. Please try refreshing.');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificates = async () => {
    try {
      const response = await axiosInstance.get('/admin/download-certificates');
      const certificates = response.data;
      
      if (certificates.length === 0) {
        toast.info(t('adminToasts.NoCertificatesAvailableToDownload'));
        return;
      }
      
      // Convert to CSV
      const headers = ['Employee Name', 'Email', 'Course', 'Issued Date', 'Certificate ID'];
      const csvContent = [
        headers.join(','),
        ...certificates.map(cert => [
          cert.userName,
          cert.userEmail,
          cert.courseTitle,
          new Date(cert.issuedAt).toLocaleDateString(),
          cert.id
        ].join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificates_${analytics?.company?.name || 'company'}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${certificates.length} certificates`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('adminToasts.FailedToDownloadCertificates'));
    }
  };

  // Forms Management Functions
  const loadForms = async () => {
    try {
      const response = await axiosInstance.get('/forms');
      setForms(response.data);
    } catch (error) {
      console.error('Failed to load forms:', error);
      toast.error(t('adminToasts.FailedToLoadForms'));
    }
  };

  const loadCompaniesForSuperAdmin = async () => {
    try {
      if (user.role === 'super_admin') {
        const response = await axiosInstance.get('/superadmin/companies');
        setAllCompanies(response.data);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const addFormField = () => {
    if (!newField.label) {
      toast.error(t('adminToasts.FieldLabelIsRequired'));
      return;
    }
    
    const field = {
      ...newField,
      id: Date.now().toString(),
      options: newField.type === 'select' || newField.type === 'radio' || newField.type === 'checkbox' 
        ? newField.options.filter(opt => opt.trim()) 
        : []
    };
    
    setFormData({
      ...formData,
      fields: [...formData.fields, field]
    });
    
    setNewField({
      label: '',
      type: 'text',
      required: false,
      options: []
    });
  };

  const removeFormField = (index) => {
    const updatedFields = formData.fields.filter((_, i) => i !== index);
    setFormData({ ...formData, fields: updatedFields });
  };

  const createForm = async () => {
    try {
      if (!formData.title || formData.fields.length === 0) {
        toast.error(t('adminToasts.FormTitleAndAtLeastOneFieldAreRequired'));
        return;
      }

      // For super admin, they need to specify a company
      let payload = { ...formData };
      if (user.role === 'super_admin') {
        if (!formData.companyId) {
          toast.error(t('adminToasts.PleaseSelectACompanyForThisForm'));
          return;
        }
      }

      await axiosInstance.post('/forms', payload);
      toast.success(t('adminToasts.FormCreatedSuccessfully'));
      
      setFormData({ title: '', description: '', fields: [], companyId: '' });
      setShowCreateFormDialog(false);
      await loadForms();
    } catch (error) {
      console.error('Failed to create form:', error);
      toast.error(error.response?.data?.detail || 'Failed to create form');
    }
  };

  const viewFormSubmissions = async (form) => {
    try {
      setSelectedForm(form);
      const response = await axiosInstance.get(`/forms/${form.id}/submissions`);
      setFormSubmissions(response.data);
      setShowFormSubmissionsDialog(true);
    } catch (error) {
      console.error('Failed to load submissions:', error);
      toast.error(t('adminToasts.FailedToLoadFormSubmissions'));
    }
  };

  const toggleFormActive = async (formId) => {
    try {
      const response = await axiosInstance.patch(`/forms/${formId}/toggle-active`);
      toast.success(response.data.message);
      await loadForms();
    } catch (error) {
      console.error('Failed to toggle form status:', error);
      toast.error(t('adminToasts.FailedToUpdateFormStatus'));
    }
  };

  // PDF Forms Management Functions
  const loadPdfForms = async () => {
    try {
      const response = await axiosInstance.get('/pdf-forms');
      setPdfForms(response.data);
    } catch (error) {
      console.error('Failed to load PDF forms:', error);
      toast.error(t('adminToasts.FailedToLoadPdfForms'));
    }
  };

  const createPdfForm = async () => {
    try {
      if (!pdfFormData.title) {
        toast.error(t('adminToasts.PdfFormTitleIsRequired'));
        return;
      }

      if (!selectedPdfFile) {
        toast.error(t('adminToasts.PleaseSelectAPdfFileToUpload'));
        return;
      }

      // For super admin, they need to select a company
      if (user.role === 'super_admin') {
        if (!pdfFormData.companyId) {
          toast.error(t('adminToasts.PleaseSelectACompanyForThisPdfForm'));
          return;
        }
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedPdfFile);
      formData.append('title', pdfFormData.title);
      formData.append('description', pdfFormData.description || '');
      
      if (user.role === 'super_admin') {
        formData.append('companyId', pdfFormData.companyId);
      }

      await axiosInstance.post('/pdf-forms', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success(t('adminToasts.PdfFormCreatedSuccessfully'));
      
      setPdfFormData({ title: '', description: '', companyId: '' });
      setSelectedPdfFile(null);
      setShowCreatePdfFormDialog(false);
      await loadPdfForms();
    } catch (error) {
      console.error('Failed to create PDF form:', error);
      toast.error(error.response?.data?.detail || 'Failed to create PDF form');
    }
  };

  const viewPdfSubmissions = async (pdfForm) => {
    try {
      setSelectedPdfForm(pdfForm);
      const response = await axiosInstance.get(`/pdf-forms/${pdfForm.id}/submissions`);
      setPdfSubmissions(response.data);
      setShowPdfSubmissionsDialog(true);
    } catch (error) {
      console.error('Failed to load PDF submissions:', error);
      toast.error(t('adminToasts.FailedToLoadPdfFormSubmissions'));
    }
  };

  const togglePdfFormActive = async (formId) => {
    try {
      const response = await axiosInstance.patch(`/pdf-forms/${formId}/toggle-active`);
      toast.success(response.data.message);
      await loadPdfForms();
    } catch (error) {
      console.error('Failed to toggle PDF form status:', error);
      toast.error(t('adminToasts.FailedToUpdatePdfFormStatus'));
    }
  };


  const deletePdfForm = async (formId, formTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${formTitle}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/pdf-forms/${formId}`);
      toast.success(t('adminToasts.PdfFormDeletedSuccessfully'));
      await loadPdfForms();
    } catch (error) {
      console.error('Failed to delete PDF form:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete PDF form');
    }
  };


  // Download form submissions as CSV
  const downloadFormSubmissions = async (formId, formTitle) => {
    try {
      const response = await axiosInstance.get(`/forms/${formId}/submissions/download`, {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formTitle.replace(/[^a-z0-9]/gi, '_')}_submissions.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('adminToasts.FormSubmissionsDownloadedSuccessfully'));
    } catch (error) {
      console.error('Failed to download submissions:', error);
      if (error.response?.status === 404) {
        toast.error(t('adminToasts.NoSubmissionsFoundForThisForm'));
      } else {
        toast.error(t('adminToasts.FailedToDownloadFormSubmissions'));
      }
    }
  };

  // Download PDF form submissions as CSV
  const downloadPdfFormSubmissions = async (formId, formTitle) => {
    try {
      const response = await axiosInstance.get(`/pdf-forms/${formId}/submissions/download`, {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formTitle.replace(/[^a-z0-9]/gi, '_')}_submissions.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('adminToasts.PdfFormSubmissionsDownloadedSuccessfully'));
    } catch (error) {
      console.error('Failed to download PDF submissions:', error);
      if (error.response?.status === 404) {
        toast.error(t('adminToasts.NoSubmissionsFoundForThisPdfForm'));
      } else {
        toast.error(t('adminToasts.FailedToDownloadPdfFormSubmissions'));
      }
    }
  };

  // Download individual completed PDF
  const downloadCompletedPdf = async (formId, submissionId, submitterName, formTitle) => {
    try {
      const response = await axiosInstance.get(`/pdf-forms/${formId}/submissions/${submissionId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${formTitle}_${submitterName}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('adminToasts.CompletedPdfDownloadedSuccessfully'));
    } catch (error) {
      console.error('Failed to download completed PDF:', error);
      if (error.response?.status === 404) {
        toast.error(t('adminToasts.CompletedPdfNotFound'));
      } else {
        toast.error(t('adminToasts.FailedToDownloadCompletedPdf'));
      }
    }
  };

  // Worker ID Functions removed - Worker IDs now accessible via File Manager

  // Toolbox Talks Functions
  const loadToolboxTalks = async () => {
    try {
      const response = await axiosInstance.get('/toolbox-talks');
      setToolboxTalks(response.data);
    } catch (error) {
      console.error('Failed to load toolbox talks:', error);
    }
  };

  const loadCloudStorageConfig = async () => {
    try {
      const response = await axiosInstance.get('/admin/cloud-storage');
      setCloudStorageConfig(response.data);
    } catch (error) {
      console.error('Failed to load cloud storage config:', error);
    }
  };


  // Message Functions
  const sendMessageToWorkers = async () => {
    try {
      let recipientIds = [];
      
      if (messageForm.recipients === 'all') {
        recipientIds = users.filter(u => u.role === 'worker' || u.role === 'learner').map(u => u.id);
      } else if (messageForm.recipients === 'incomplete') {
        // Get users with incomplete training
        const allUsers = users.filter(u => u.role === 'worker' || u.role === 'learner');
        // Filter users who don't have all course certificates
        recipientIds = allUsers.filter(u => {
          // This is simplified - in production you'd check actual progress
          return true; // Send to all for now
        }).map(u => u.id);
      } else if (messageForm.recipients === 'specific') {
        recipientIds = selectedUsers;
      }
      
      if (recipientIds.length === 0) {
        toast.error(t('adminToasts.NoRecipientsSelected'));
        return;
      }
      
      await axiosInstance.post('/messages/send', {
        toUserIds: recipientIds,
        subject: messageForm.subject,
        message: messageForm.message,
        type: 'reminder'
      });
      
      toast.success(`Message sent to ${recipientIds.length} worker(s)!`);
      setShowMessageDialog(false);
      setMessageForm({ subject: '', message: '', recipients: 'all' });
      setSelectedUsers([]);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  // Training Reminder Functions
  const sendTrainingReminders = async () => {
    try {
      setSendingReminders(true);
      
      // Prepare reminder data
      const reminderData = {
        recipientType: reminderForm.recipientType,
        messageType: reminderForm.messageType,
        customMessage: reminderForm.customMessage,
        userIds: reminderForm.userIds
      };
      
      const response = await axiosInstance.post('/admin/send-training-reminders', reminderData);
      
      toast.success(`Training reminders sent to ${response.data.sent} worker(s)!`);
      
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} email(s) failed to send`);
      }
      
      setShowReminderDialog(false);
      setReminderForm({
        recipientType: 'all',
        messageType: 'general',
        customMessage: '',
        userIds: []
      });
    } catch (error) {
      console.error('Failed to send training reminders:', error);
      toast.error(error.response?.data?.detail || 'Failed to send training reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const createToolboxTalk = async () => {
    try {
      setUploadingTalk(true);
      await axiosInstance.post('/admin/toolbox-talks', talkForm);
      toast.success(t('adminToasts.ToolboxTalkCreatedSuccessfully'));
      setShowCreateTalkDialog(false);
      setTalkForm({ title: '', description: '', content: '', category: '' });
      loadToolboxTalks();
    } catch (error) {
      console.error('Failed to create toolbox talk:', error);
      toast.error(error.response?.data?.detail || 'Failed to create toolbox talk');
    } finally {
      setUploadingTalk(false);
    }
  };

  const uploadToolboxTalkPdf = async () => {
    if (!selectedTalkPdf) {
      toast.error(t('adminToasts.PleaseSelectAPdfFile'));
      return;
    }

    try {
      setUploadingTalk(true);
      const formData = new FormData();
      formData.append('file', selectedTalkPdf);
      formData.append('title', talkForm.title);
      formData.append('description', talkForm.description || '');
      formData.append('category', talkForm.category || '');

      await axiosInstance.post('/admin/toolbox-talks/upload-pdf', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t('adminToasts.ToolboxTalkPdfUploadedSuccessfully'));
      setShowUploadTalkDialog(false);
      setTalkForm({ title: '', description: '', content: '', category: '' });
      setSelectedTalkPdf(null);
      loadToolboxTalks();
    } catch (error) {
      console.error('Failed to upload toolbox talk PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploadingTalk(false);
    }
  };

  const deleteToolboxTalk = async (talkId) => {
    if (!window.confirm('Are you sure you want to delete this toolbox talk?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/toolbox-talks/${talkId}`);
      toast.success(t('adminToasts.ToolboxTalkDeletedSuccessfully'));
      loadToolboxTalks();
    } catch (error) {
      console.error('Failed to delete toolbox talk:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete toolbox talk');
    }
  };

  const viewToolboxTalk = (talk) => {
    setSelectedTalk(talk);
    setShowViewTalkDialog(true);
  };

  const downloadToolboxTalkPdf = async (talk) => {
    try {
      const response = await axiosInstance.get(`/toolbox-talks/${talk.id}/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${talk.title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('adminToasts.PdfDownloadedSuccessfully'));
    } catch (error) {
      console.error('Failed to download PDF:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to download PDF';
      toast.error(errorMsg);
    }
  };

  // Cloud Storage functions
  const saveCloudStorageConfig = async () => {
    if (!cloudStorageForm.provider || !cloudStorageForm.accessKey || !cloudStorageForm.secretKey || !cloudStorageForm.bucketName) {
      toast.error(t('adminToasts.PleaseFillInAllRequiredFields'));
      return;
    }

    try {
      setSavingCloudStorage(true);
      await axiosInstance.post('/admin/cloud-storage', cloudStorageForm);
      toast.success(t('adminToasts.CloudStorageConfigurationSavedSuccessfully'));
      setShowCloudStorageDialog(false);
      loadCloudStorageConfig();
    } catch (error) {
      console.error('Failed to save cloud storage config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingCloudStorage(false);
    }
  };

  const deleteCloudStorageConfig = async () => {
    if (!window.confirm('Are you sure you want to delete your cloud storage configuration? Uploaded files will no longer be accessible.')) {
      return;
    }

    try {
      await axiosInstance.delete('/admin/cloud-storage');
      toast.success(t('adminToasts.CloudStorageConfigurationDeleted'));
      setCloudStorageConfig(null);
    } catch (error) {
      console.error('Failed to delete cloud storage config:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete configuration');
    }
  };

  // Incident Reports Functions
  const loadIncidentReports = async () => {
    try {
      const response = await axiosInstance.get('/incident-reports');
      setIncidentReports(response.data);
    } catch (error) {
      console.error('Failed to load incident reports:', error);
      toast.error(t('adminToasts.FailedToLoadIncidentReports'));
    }
  };

  const handleViewIncident = (report) => {
    console.log('handleViewIncident called for report:', report.id, report.incidentType);
    // Close any other open dialogs to prevent overlay interference
    setShowFilesDialog(false);
    setShowJsaDialog(false);
    setShowToolboxSessionsDialog(false);
    // Set the selected incident and open its dialog
    setSelectedIncident(report);
    setTimeout(() => {
      setShowIncidentReportsDialog(true);
    }, 50);
  };

  const handleExportAllToolboxPDFs = async () => {
    if (toolboxSessions.length === 0) {
      toast.error(t('adminToasts.NoToolboxSessionsToExport'));
      return;
    }

    toast.info(`Exporting ${toolboxSessions.length} toolbox talk PDFs...`);
    
    let successCount = 0;
    let failCount = 0;

    for (const session of toolboxSessions) {
      try {
        const talk = toolboxTalks.find(t => t.id === session.toolboxTalkId);
        const response = await axiosInstance.get(
          `/toolbox-talk-sessions/${session.id}/export`,
          { responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `toolbox_talk_${talk?.title?.replace(/\s+/g, '_') || 'session'}_${new Date(session.conductedAt).toLocaleDateString().replace(/\//g, '-')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        successCount++;
        
        // Small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Failed to export session:', session.id, error);
        failCount++;
      }
    }

    if (failCount === 0) {
      toast.success(`Successfully exported all ${successCount} toolbox talk PDFs`);
    } else {
      toast.warning(`Exported ${successCount} PDFs, ${failCount} failed`);
    }
  };

  const handleUpdateIncidentStatus = async (reportId, newStatus, reviewNotes = '') => {
    try {
      setUpdatingIncidentStatus(true);
      const response = await axiosInstance.put(`/incident-reports/${reportId}`, {
        status: newStatus,
        reviewNotes: reviewNotes || undefined
      });
      // Backend now returns the updated report directly
      if (response.data && selectedIncident?.id === reportId) {
        setSelectedIncident(response.data);
      }
      toast.success(t('adminToasts.StatusUpdatedSuccessfully'));
      loadIncidentReports();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingIncidentStatus(false);
    }
  };

  const handleDownloadIncidentPdf = async (reportId) => {
    try {
      const response = await axiosInstance.get(`/incident-reports/${reportId}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incident_report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('adminToasts.PdfDownloaded'));
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error(t('adminToasts.FailedToDownloadPdf'));
    }
  };

  const handleDeleteIncident = async (reportId, incidentType) => {
    if (!window.confirm(`Are you sure you want to delete this incident report?\n\nType: ${incidentType}\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/incident-reports/${reportId}`);
      toast.success(t('adminToasts.IncidentReportDeletedSuccessfully'));
      setShowIncidentReportsDialog(false);
      loadIncidentReports();
    } catch (error) {
      console.error('Failed to delete incident report:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete incident report');
    }
  };

  const handleExportAllIncidents = async () => {
    try {
      const response = await axiosInstance.post('/incident-reports/export-all', {}, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'all_incident_reports.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('adminToasts.AllReportsExported'));
    } catch (error) {
      console.error('Failed to export reports:', error);
      toast.error(t('adminToasts.FailedToExportReports'));
    }
  };

  // JSA Functions
  const loadSubmittedJsas = async () => {
    setLoadingJsas(true);
    try {
      const response = await axiosInstance.get('/jsa/submitted/list');
      setSubmittedJsas(response.data);
    } catch (error) {
      console.error('Failed to load submitted JSAs:', error);
      toast.error(t('adminToasts.FailedToLoadSubmittedJsas'));
    } finally {
      setLoadingJsas(false);
    }
  };

  const handleViewJsa = (jsa) => {
    console.log('Opening JSA view dialog:', jsa.id, jsa.taskDescription);
    setSelectedJsa(jsa);
    setShowJsaDialog(true);
  };

  const handleDownloadJsaPdf = async (jsaId, taskDescription) => {
    try {
      console.log('Downloading PDF for JSA:', jsaId, 'Task:', taskDescription);
      // Add cache-busting timestamp
      const timestamp = new Date().getTime();
      const response = await axiosInstance.get(`/jsa/${jsaId}/generate-pdf?t=${timestamp}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = `JSA_${taskDescription.replace(/[^a-z0-9]/gi, '_').substring(0, 30)}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`JSA PDF downloaded: ${taskDescription}`);
    } catch (error) {
      console.error('Failed to download JSA PDF:', error);
      toast.error(t('adminToasts.FailedToDownloadJsaPdf'));
    }
  };

  const handleDeleteJsa = async (jsaId, taskDescription) => {
    if (!window.confirm(`Are you sure you want to delete JSA: ${taskDescription}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Deleting JSA:', jsaId);
      await axiosInstance.delete(`/jsa/${jsaId}`);
      toast.success(`JSA deleted: ${taskDescription}`);
      // Refresh the JSA list
      loadSubmittedJsas();
    } catch (error) {
      console.error('Failed to delete JSA:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete JSA');
    }
  };

  const filteredIncidentReports = incidentFilter === 'all' 
    ? incidentReports 
    : incidentReports.filter(r => r.status === incidentFilter);



  const loadBillingInfo = async () => {
    try {
      const response = await axiosInstance.get('/billing/pricing');
      setBillingInfo(response.data);
    } catch (error) {
      console.error('Failed to load billing info:', error);
    }
  };

  const handlePayNow = () => {
    // Get current active employee count as default
    const activeCount = (users || []).filter(u => u.isActive).length;
    setLicenseCount(activeCount.toString());
    calculatePricing(activeCount);
    setShowLicenseDialog(true);
  };

  const calculatePricing = (count, discount = appliedDiscount) => {
    const numCount = parseInt(count) || 0;
    
    // Pricing tiers (updated)
    let pricePerLearner = 100;
    if (numCount >= 26 && numCount <= 100) pricePerLearner = 90;
    else if (numCount >= 101 && numCount <= 250) pricePerLearner = 80;
    else if (numCount >= 251) pricePerLearner = 70;
    
    const subtotal = numCount * pricePerLearner;
    let discountAmount = 0;
    let subtotalAfterDiscount = subtotal;
    
    if (discount) {
      discountAmount = subtotal * (discount.percentage / 100);
      subtotalAfterDiscount = subtotal - discountAmount;
    }
    
    // Tax removed - total is subtotal after discount
    const total = subtotalAfterDiscount;
    
    setCalculatedPricing({
      count: numCount,
      pricePerLearner,
      subtotal,
      discountAmount,
      total,
      discount,
      tier: numCount >= 251 ? '251+ learners' :
            numCount >= 101 ? '101-250 learners' :
            numCount >= 26 ? '26-100 learners' :
            '1-25 learners'
    });
  };

  const handleLicenseCountChange = (e) => {
    let value = e.target.value;
    
    // Enforce max limit of 500
    if (value && parseInt(value) > 500) {
      value = '500';
      toast.error(t('adminToasts.MaximumLicensesAllowed'));
    }
    
    setLicenseCount(value);
    if (value && parseInt(value) > 0) {
      calculatePricing(parseInt(value));
    }
  };

  // Discount code validation
  const validateDiscountCode = async (code) => {
    if (!code.trim()) {
      setAppliedDiscount(null);
      return;
    }

    setValidatingDiscount(true);
    try {
      const response = await axiosInstance.post('/billing/validate-discount', {
        code: code.trim().toUpperCase()
      });
      
      const discount = response.data.discount;
      setAppliedDiscount(discount);
      toast.success(`${discount.code} applied! ${discount.percentage}% off`);
      
      // Recalculate pricing with discount
      if (licenseCount && parseInt(licenseCount) > 0) {
        calculatePricing(parseInt(licenseCount), discount);
      }
    } catch (error) {
      setAppliedDiscount(null);
      toast.error(error.response?.data?.detail || 'Invalid discount code');
      
      // Recalculate without discount
      if (licenseCount && parseInt(licenseCount) > 0) {
        calculatePricing(parseInt(licenseCount), null);
      }
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleDiscountCodeChange = (e) => {
    const code = e.target.value;
    setDiscountCode(code);
    
    // Auto-validate when user stops typing (debounced)
    clearTimeout(window.discountTimer);
    window.discountTimer = setTimeout(() => {
      validateDiscountCode(code);
    }, 800);
  };

  const handleProceedToPayment = async () => {
    const count = parseInt(licenseCount);
    
    if (!licenseCount || count < 1) {
      toast.error(t('adminToasts.PleaseEnterAValidNumberOfLicensesMinimum'));
      return;
    }
    
    if (count > 500) {
      toast.error(t('adminToasts.MaximumLicensesAllowed'));
      return;
    }

    try {
      const originUrl = window.location.origin;
      const requestData = {
        origin_url: originUrl,
        license_count: count
      };
      
      // Include discount code if applied
      if (appliedDiscount) {
        requestData.discount_code = appliedDiscount.code;
      }
      
      const response = await axiosInstance.post('/billing/create-checkout', requestData);
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handleTransferWorker = async () => {
    if (!destinationCompanyCode.trim()) {
      toast.error(t('adminToasts.PleaseEnterDestinationCompanyCode'));
      return;
    }

    try {
      const response = await axiosInstance.post('/companies/transfer-worker', {
        workerId: transferWorker.id,
        destinationCompanyCode: destinationCompanyCode.toUpperCase()
      });

      toast.success(`${transferWorker.name} transferred to ${response.data.to.companyName}`);
      setShowTransferDialog(false);
      setTransferWorker(null);
      setDestinationCompanyCode('');
      loadData();
      loadBillingInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    }
  };

  const handleToggleWorkerStatus = async (worker) => {
    const action = worker.isActive ? 'deactivate' : 'activate';
    const confirmMsg = worker.isActive 
      ? `Deactivate ${worker.name}? They will not be able to login and won't count toward billing.`
      : `Reactivate ${worker.name}? They will be able to login and will count toward billing.`;
    
    if (!confirm(confirmMsg)) return;

    try {
      const response = await axiosInstance.post('/companies/toggle-worker-status', {
        workerId: worker.id
      });

      toast.success(response.data.message);
      loadData();
      loadBillingInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} worker`);
    }
  };
  
  // Edit User Functions
  const openEditUserDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditUserForm({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      password: '', // Always start empty for security
      phone: userToEdit.phone || '',
      role: userToEdit.role || '',
      isActive: userToEdit.isActive !== undefined ? userToEdit.isActive : true
    });
    setShowEditUserDialog(true);
  };
  
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      // Only send fields that have values
      const updateData = {};
      if (editUserForm.name) updateData.name = editUserForm.name;
      if (editUserForm.email) updateData.email = editUserForm.email;
      if (editUserForm.password) updateData.password = editUserForm.password;
      if (editUserForm.phone) updateData.phone = editUserForm.phone;
      if (editUserForm.role) updateData.role = editUserForm.role;
      updateData.isActive = editUserForm.isActive;
      
      const response = await axiosInstance.patch(`/admin/users/${editingUser.id}`, updateData);
      
      toast.success(t('adminToasts.UserUpdatedSuccessfully'));
      setShowEditUserDialog(false);
      setEditingUser(null);
      loadData(); // Reload user list
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Filter users based on search query
  const filteredUsers = (users || []).filter(u => {
    if (!userSearchQuery.trim()) return true;
    
    const query = userSearchQuery.toLowerCase();
    return (
      u.name.toLowerCase().includes(query) ||
      u.email.toLowerCase().includes(query) ||
      u.role.toLowerCase().includes(query)
    );
  });
  
  // Create User Handler
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validate craft selection for workers
    if (createUserForm.role === 'worker' && !createUserForm.craft) {
      toast.error(t('adminToasts.PleaseSelectACraftForTheWorker'));
      return;
    }
    
    try {
      const response = await axiosInstance.post('/admin/users', createUserForm);
      toast.success(response.data.message || 'User created successfully!');
      
      // Reset form
      setCreateUserForm({
        name: '',
        email: '',
        password: '',
        role: 'worker',
        phone: '',
        craft: ''
      });
      setShowCreateUserDialog(false);
      
      // Reload user list
      loadData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Course management functions removed

  // Quiz management functions removed
  // Classroom Learning Handlers
  const handleAddStudent = async (e) => {
    e.preventDefault();
    
    // Validate craft selection
    if (!studentForm.craft) {
      toast.error(t('adminToasts.PleaseSelectACraftForTheStudent'));
      return;
    }
    
    try {
      const response = await axiosInstance.post('/admin/classroom/students', {
        ...studentForm,
        companyId: user.companyId
      });
      
      toast.success(`Student ${studentForm.name} added successfully with login access`);
      setStudentForm({ name: '', email: '', phone: '', position: '', password: '', craft: '' });
      setShowStudentDialog(false);
      loadClassroomStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    }
  };

  const handleAddExistingUser = async () => {
    if (!selectedExistingUser) {
      toast.error(t('adminToasts.PleaseSelectAUser'));
      return;
    }

    try {
      // Add the existing user to classroom students
      const response = await axiosInstance.post('/admin/classroom/add-existing', {
        userId: selectedExistingUser
      });
      
      toast.success(t('adminToasts.UserAddedToClassroomSuccessfully'));
      setShowAddExistingUserDialog(false);
      setSelectedExistingUser(null);
      loadClassroomStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user to classroom');
    }
  };

  const handleMarkPassed = async () => {
    if (!selectedStudent || !selectedCourse) {
      toast.error(t('adminToasts.PleaseSelectBothStudentAndCourse'));
      return;
    }

    try {
      await axiosInstance.post('/admin/classroom/mark-passed', {
        studentId: selectedStudent.id,
        courseId: selectedCourse.id,
        companyId: user.companyId
      });
      
      toast.success(`${selectedStudent.name} marked as passed for ${selectedCourse.title}. Certificate generated!`);
      setShowPassDialog(false);
      setSelectedStudent(null);
      setSelectedCourse(null);
      loadClassroomStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as passed');
    }
  };

  const loadClassroomStudents = async () => {
    try {
      const response = await axiosInstance.get('/admin/classroom/students');
      setClassroomStudents(response.data);
    } catch (error) {
      console.error('Failed to load classroom students:', error);
    }
  };

  // Load classroom students on component mount
  useEffect(() => {
    if (user.role === 'company_admin') {
      loadClassroomStudents();
    }
  }, [user.role]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-yellow-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                <HardHat className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">{t('adminUI.SafetymindAiAdmin')}</h1>
                <p className="text-sm text-slate-600">Welcome, {user.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <NotificationBell />
              <Button
                onClick={() => window.location.href = '/craft-management'}
                variant="outline"
                className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
              >
                <HardHat className="w-4 h-4" />
                <span>{t('adminUI.ManageCrafts')}</span>
              </Button>
              <Button
                onClick={downloadCertificates}
                variant="outline"
                className="flex items-center space-x-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
              >
                <Download className="w-4 h-4" />
                <span>{t('adminUI.DownloadCertificates')}</span>
              </Button>
              <Button
                onClick={onLogout}
                variant="outline"
                data-testid="logout-button-admin"
                className="flex items-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                <span>{t('adminUI.Logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <p className="mt-4 text-slate-600">{t('adminUI.LoadingDashboard')}</p>
          </div>
        )}

        {/* Error State - No Data Loaded */}
        {!loading && !analytics && (
          <Card className="mb-8 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-800 mb-2">{t('adminUI.UnableToLoadDashboardData')}</h3>
                <p className="text-red-600 mb-4">
                  {user.role === 'worker' 
                    ? 'You do not have permission to access the admin dashboard. Please use the Worker Dashboard instead.' 
                    : 'There was a problem loading your dashboard information.'}
                </p>
                {user.role === 'worker' ? (
                  <Button 
                    onClick={() => window.location.href = '/dashboard'}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Go to Worker Dashboard
                  </Button>
                ) : (
                  <Button 
                    onClick={loadData}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >{t('adminUI.TryAgain')}</Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        {!loading && analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="card-hover">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  {t('admin.totalUsers')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{analytics.totalUsers || 0}</div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
                  <BookOpen className="w-4 h-4 mr-2" />
                  {t('admin.totalCourses')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800">{analytics.totalCourses || 0}</div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {t('admin.completions')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{analytics.totalCompletions || 0}</div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
                  <Award className="w-4 h-4 mr-2" />
                  {t('admin.certificates')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{analytics.totalCertificates || 0}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Near Miss Dashboard Section */}
        {!loading && analytics && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
                Near Miss Reports
              </h2>
            </div>
            <NearMissDashboard />
          </div>
        )}

        {/* Company Training Management */}
        {!loading && analytics && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'gc_admin') && (
          <div className="mb-8">
            <CompanyTrainingManager />
          </div>
        )}

        {/* Company Settings - Required Courses */}
        {user.role === 'company_admin' && analytics.company && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{t('admin.companySettings')} - {analytics.company.name}</CardTitle>
              <CardDescription>{t('adminUI.ConfigureRequiredCoursesForYourEmployees')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-semibold">{t('adminUI.RequiredCourses')}</Label>
                  <p className="text-sm text-slate-600 mb-4">{t('adminUI.ClickMultipleCoursesToMarkAsRequiredClickSaveChangesWhenDone')}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto p-4 bg-slate-50 rounded-lg">
                  {allCourses.map(course => {
                    const currentSelection = selectedRequiredCourses !== null ? selectedRequiredCourses : (analytics.company.requiredCourses || []);
                    const isRequired = currentSelection.includes(course.id);
                    return (
                      <div
                        key={course.id}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          isRequired 
                            ? 'border-orange-500 bg-orange-50' 
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        onClick={() => {
                          const newRequired = isRequired
                            ? currentSelection.filter(id => id !== course.id)
                            : [...currentSelection, course.id];
                          
                          console.log('Course clicked:', course.title);
                          console.log('Was required:', isRequired);
                          console.log('Current selection:', currentSelection.length, 'courses');
                          console.log('New selection:', newRequired.length, 'courses');
                          
                          setSelectedRequiredCourses(newRequired);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm">{course.title}</h4>
                            <p className="text-xs text-slate-600 mt-1">{course.category} • {course.duration} min</p>
                          </div>
                          {isRequired && (
                            <Badge className="bg-orange-500 text-white">{t('adminUI.Required')}</Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-slate-600">
                    {(selectedRequiredCourses !== null ? selectedRequiredCourses : (analytics.company.requiredCourses || [])).length} of {allCourses.length} courses selected
                  </p>
                  <div className="flex gap-2">
                    {(selectedRequiredCourses !== null ? selectedRequiredCourses : (analytics.company.requiredCourses || [])).length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedRequiredCourses([]);
                        }}
                      >
                        Clear All
                      </Button>
                    )}
                    <Button
                      className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
                      size="sm"
                      onClick={async () => {
                        try {
                          const coursesToSave = selectedRequiredCourses !== null 
                            ? selectedRequiredCourses 
                            : analytics.company.requiredCourses || [];
                          
                          console.log('=== SAVING REQUIRED COURSES ===');
                          console.log('Courses to save:', coursesToSave.length);
                          console.log('Course IDs:', coursesToSave);
                          
                          await axiosInstance.put('/companies/required-courses', {
                            courseIds: coursesToSave
                          });
                          
                          console.log('Save successful, reloading data...');
                          
                          // Reset selection state first
                          setSelectedRequiredCourses(null);
                          
                          // Reload data and wait for it
                          await loadData();
                          
                          toast.success(`Required courses updated! ${coursesToSave.length} courses selected.`);
                        } catch (error) {
                          console.error('Failed to save required courses:', error);
                          toast.error(error.response?.data?.detail || 'Failed to update required courses');
                        }
                      }}
                      disabled={selectedRequiredCourses === null}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cloud Storage Configuration */}
        {user.role === 'company_admin' && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{t('admin.cloudStorage')}</CardTitle>
              <CardDescription>
                Configure your own cloud storage for persistent file uploads (PDFs, documents, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {cloudStorageConfig ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-green-900">✅ Cloud Storage Configured</h4>
                        <p className="text-sm text-green-700 mt-1">
                          Provider: <strong>{cloudStorageConfig.provider.replace('_', ' ').toUpperCase()}</strong>
                        </p>
                        <p className="text-sm text-green-700">
                          Bucket: <strong>{cloudStorageConfig.bucketName}</strong>
                        </p>
                        {cloudStorageConfig.region && (
                          <p className="text-sm text-green-700">
                            Region: <strong>{cloudStorageConfig.region}</strong>
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCloudStorageForm({
                              provider: cloudStorageConfig.provider,
                              accessKey: cloudStorageConfig.accessKey,
                              secretKey: '',
                              bucketName: cloudStorageConfig.bucketName,
                              region: cloudStorageConfig.region || '',
                              customEndpoint: cloudStorageConfig.customEndpoint || ''
                            });
                            setShowCloudStorageDialog(true);
                          }}
                        >
                          Update
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={deleteCloudStorageConfig}
                          className="text-red-600 hover:text-red-700"
                        >{t('adminUI.Delete')}</Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-semibold text-yellow-900">⚠️ No Cloud Storage Configured</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Files are currently stored locally and will be lost on deployment. Configure cloud storage for persistence.
                    </p>
                  </div>
                  <Button onClick={() => setShowCloudStorageDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Configure Cloud Storage
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* New Hires - Dedicated Tile */}
        {(user.role === 'company_admin' || user.role === 'super_admin') && (
          <Card className="mb-8 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-900">
                <UserPlus className="w-5 h-5 text-green-600" />
                New Hire Paperwork
              </CardTitle>
              <CardDescription>{t('adminUI.WorkerOnboardingDocumentsTaxFormsAndIdVerification')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-gray-600">{t('adminUI.AccessAllNewHireDocumentsOrganizedByWorker')}</p>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Personal Information (onboarding forms)</li>
                  <li>• Tax Forms (W-4, I-9, state forms + ID pictures)</li>
                  <li>• Individual document downloads</li>
                </ul>
                <Button 
                  onClick={() => navigate('/admin/new-hires')}
                  className="w-full bg-green-600 hover:bg-green-700"
                  size="lg"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  View New Hire Documents
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* File Manager */}
        {(user.role === 'company_admin' || user.role === 'super_admin') && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                File Manager
              </CardTitle>
              <CardDescription>{t('adminUI.CompanyDocumentsAndGeneralFileStorage')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button onClick={async () => {
                  try {
                    const response = await axiosInstance.get('/admin/files');
                    const { files, totalFiles, totalSize } = response.data;
                    
                    if (totalFiles === 0) {
                      toast.info(t('adminToasts.NoFilesUploadedYet'));
                      return;
                    }
                    
                    // Show files in a simple list with download buttons
                    const fileList = files.map(f => ({
                      ...f,
                      sizeKB: (f.size / 1024).toFixed(2)
                    }));
                    
                    // Store in state to display
                    setUploadedFiles(fileList);
                    setShowFilesDialog(true);
                  } catch (error) {
                    console.error('Failed to load files:', error);
                    toast.error(t('adminToasts.FailedToLoadFiles'));
                  }
                }}>
                  <Download className="w-4 h-4 mr-2" />
                  View All Files
                </Button>
                
                <Button 
                  onClick={() => navigate('/admin/sds-library')}
                  variant="outline"
                  className="gap-2"
                >
                  <Beaker className="w-4 h-4" />
                  SDS Library
                </Button>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Daily Field Log - Admin View */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-teal-600" />
                  {t('fieldLogs.title')}
                </CardTitle>
                <CardDescription>{t('fieldLogs.subtitle')}</CardDescription>
              </div>
              <Button onClick={() => navigate('/admin/field-logs')} className="bg-teal-600 hover:bg-teal-700" data-testid="admin-field-logs-btn">
                <Eye className="w-4 h-4 mr-2" />
                {t('common.viewAll')}
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Company Audit Dashboard Link */}
        {(user.role === 'company_admin' || user.role === 'super_admin') && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart className="w-5 h-5" />
                Company Audit Dashboard
              </CardTitle>
              <CardDescription>
                View comprehensive audit analytics, site risk heatmaps, and AI-powered safety insights
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">{t('adminUI.AccessDetailedAuditAnalyticsIncluding')}</p>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                  <li>Company safety score with risk analysis</li>
                  <li>Site risk heatmaps by location</li>
                  <li>Repeated hazards and trends</li>
                  <li>Supervisor performance metrics</li>
                  <li>AI-powered recommendations</li>
                  <li>Corrective actions tracking</li>
                </ul>
                <Button 
                  onClick={() => navigate('/company/audits')}
                  className="w-full"
                >
                  <BarChart className="w-4 h-4 mr-2" />
                  Open Audit Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* JSA Update Queue Link */}
        {(user.role === 'company_admin' || user.role === 'super_admin' || user.role === 'supervisor') && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                JSA Update Queue
              </CardTitle>
              <CardDescription>{t('adminUI.ReviewAndApproveAiGeneratedJsaUpdatesFromSafetyAudits')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Closed-loop learning system that automatically generates JSA improvement suggestions based on audit findings:
                </p>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                  <li>Review AI-generated JSA update suggestions</li>
                  <li>Approve or reject proposed changes</li>
                  <li>Track applied updates and improvements</li>
                  <li>Continuous improvement of safety protocols</li>
                </ul>
                <Button 
                  onClick={() => navigate('/jsa-updates')}
                  className="w-full"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Open JSA Update Queue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Safety Analytics */}
        {(user.role === 'company_admin' || user.role === 'super_admin') && (
          <Card className="mb-8 border-orange-200 bg-gradient-to-br from-orange-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-600" />
                Safety Analytics
              </CardTitle>
              <CardDescription>{t('adminUI.ComprehensiveSafetyMetricsIncludingJsaAuditsIncidentsBodyMap')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-white rounded-lg border border-orange-100">
                <ul className="text-sm text-gray-600 space-y-1 mb-4 ml-1">
                  <li>• JSA completion trends</li>
                  <li>• Audit scores & findings</li>
                  <li>• Incident tracking</li>
                  <li>• Injury body map visualization</li>
                </ul>
                <Button 
                  onClick={() => navigate('/safety-analytics-preview')}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  View Safety Analytics
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Incident Reports Management */}
        {(user.role === 'company_admin' || user.role === 'super_admin') && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                    Incident Reports Management
                  </CardTitle>
                  <CardDescription>{t('adminUI.ViewAndManageWorkplaceIncidentReportsFromSupervisors')}</CardDescription>
                </div>
                <Button onClick={handleExportAllIncidents} variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export All PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filter Tabs */}
              <div className="flex gap-2 mb-4 border-b">
                <Button
                  variant={incidentFilter === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setIncidentFilter('all')}
                >
                  All ({incidentReports.length})
                </Button>
                <Button
                  variant={incidentFilter === 'draft' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setIncidentFilter('draft')}
                >
                  Drafts ({incidentReports.filter(r => r.status === 'draft').length})
                </Button>
                <Button
                  variant={incidentFilter === 'submitted' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setIncidentFilter('submitted')}
                >
                  Submitted ({incidentReports.filter(r => r.status === 'submitted').length})
                </Button>
                <Button
                  variant={incidentFilter === 'under_review' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setIncidentFilter('under_review')}
                >
                  Under Review ({incidentReports.filter(r => r.status === 'under_review').length})
                </Button>
                <Button
                  variant={incidentFilter === 'resolved' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setIncidentFilter('resolved')}
                >
                  Resolved ({incidentReports.filter(r => r.status === 'resolved').length})
                </Button>
              </div>

              {/* Incident Reports List */}
              {filteredIncidentReports.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>{t('adminUI.NoIncidentReportsInThisCategory')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredIncidentReports.map((report) => (
                    <div key={report.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{report.incidentType}</h3>
                            <Badge className={
                              report.severity === 'Critical' ? 'bg-red-600' :
                              report.severity === 'High' ? 'bg-orange-600' :
                              report.severity === 'Medium' ? 'bg-yellow-600' : 'bg-blue-600'
                            }>
                              {report.severity}
                            </Badge>
                            <Badge variant={
                              report.status === 'resolved' ? 'success' :
                              report.status === 'under_review' ? 'default' :
                              report.status === 'submitted' ? 'default' : 'secondary'
                            }>
                              {report.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            <span className="font-medium">{t('adminUI.Supervisor')}</span> {report.supervisorName} | 
                            <span className="ml-2">{report.location}</span> | 
                            <span className="ml-2">{report.incidentDate} {report.incidentTime}</span>
                          </p>
                          <p className="text-sm text-gray-700 line-clamp-2">{report.description}</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewIncident(report);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {report.status !== 'draft' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadIncidentPdf(report.id);
                              }}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Submitted JSAs */}
        {(user.role === 'company_admin' || user.role === 'super_admin') && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <ClipboardList className="w-5 h-5 mr-2 text-blue-600" />
                    Submitted Job Safety Analyses (JSAs)
                  </CardTitle>
                  <CardDescription>{t('adminUI.ReviewJsasCompletedAndSignedByWorkCrews')}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingJsas ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p>{t('adminUI.LoadingJsas')}</p>
                </div>
              ) : submittedJsas.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ClipboardList className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">{t('adminUI.NoSubmittedJsasYet')}</p>
                  <p className="text-sm">{t('adminUI.CompletedJsasWillAppearHereOnceAllWorkersHaveSigned')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {submittedJsas.map((jsa) => (
                    <div key={jsa.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{jsa.taskDescription}</h3>
                            <Badge className="bg-green-600">{t('adminUI.Submitted')}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
                            <p>
                              <span className="font-medium">{t('adminUI.Supervisor')}</span> {jsa.supervisorName}
                            </p>
                            <p>
                              <span className="font-medium">{t('adminUI.Location')}</span> {jsa.location || 'N/A'}
                            </p>
                            <p>
                              <span className="font-medium">{t('adminUI.Date')}</span> {new Date(jsa.date).toLocaleDateString()}
                            </p>
                            <p>
                              <span className="font-medium">{t('adminUI.Submitted')}</span> {jsa.submittedAt ? new Date(jsa.submittedAt).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <div className="flex gap-4 text-sm">
                            <span className="text-gray-600">
                              <span className="font-medium">{jsa.hazards?.length || 0}</span> Hazards
                            </span>
                            <span className="text-green-600">
                              <span className="font-medium">{Object.keys(jsa.workerAcknowledgments || {}).length}</span> Worker Signatures
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewJsa(jsa)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadJsaPdf(jsa.id, jsa.taskDescription)}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:border-red-600"
                            onClick={() => handleDeleteJsa(jsa.id, jsa.taskDescription)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pending Worker Transfers - Critical Action Item */}
        {user.role === 'company_admin' && (
          <div className="mb-8">
            <PendingTransfers />
          </div>
        )}

        {/* JSA Billing Module */}
        {user.role === 'company_admin' && (
          <>
            {/* Billing module removed - going different route */}

            {/* Incident Report Settings */}
            <IncidentReportSettings />
          </>
        )}

        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('admin.companyForms')}</CardTitle>
                <CardDescription>{t('admin.companyFormsDesc')}</CardDescription>
              </div>
              <Button onClick={() => setShowCreateFormDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Form
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {forms.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">{t('adminUI.NoFormsCreatedYet')}</p>
                <p className="text-sm text-gray-400">{t('adminUI.CreateYourFirstFormToCollectInformationFromEmployees')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('adminUI.FormTitle')}</TableHead>
                    <TableHead>{t('adminUI.Fields')}</TableHead>
                    <TableHead>{t('adminUI.Status')}</TableHead>
                    <TableHead>{t('adminUI.Submissions')}</TableHead>
                    <TableHead>{t('adminUI.Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {forms.map(form => (
                    <TableRow key={form.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{form.title}</div>
                          {form.description && (
                            <div className="text-sm text-gray-500">{form.description}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{form.fields.length} fields</TableCell>
                      <TableCell>
                        <Badge variant={form.isActive ? 'default' : 'secondary'}>
                          {form.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewFormSubmissions(form)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleFormActive(form.id)}
                          >
                            {form.isActive ? (
                              <ToggleRight className="w-4 h-4" />
                            ) : (
                              <ToggleLeft className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* PDF Forms Management */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('admin.pdfForms')}</CardTitle>
                <CardDescription>{t('admin.pdfFormsDesc')}</CardDescription>
              </div>
              <Button onClick={() => setShowCreatePdfFormDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add PDF Form
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Quick Access Tiles for Government Forms */}
            {pdfForms.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('adminUI.QuickAccessViewSubmissions')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {pdfForms.slice(0, 8).map((form) => {
                    const submissionCount = form.submissionCount || 0;
                    return (
                      <button
                        key={form.id}
                        onClick={() => navigate(`/admin/new-hires?form=${encodeURIComponent(form.title)}`)}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <Badge variant="outline" className="text-xs">
                            {submissionCount}
                          </Badge>
                        </div>
                        <div className="font-medium text-sm text-gray-900 group-hover:text-blue-700">
                          {form.title}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {submissionCount} {submissionCount === 1 ? 'submission' : 'submissions'}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {pdfForms.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">{t('adminUI.NoPdfFormsAddedYet')}</p>
                <p className="text-sm text-gray-400">{t('adminUI.AddPdfFormsLikeWIOrOtherFillableDocuments')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('adminUI.FormTitle')}</TableHead>
                    <TableHead>{t('adminUI.Status')}</TableHead>
                    <TableHead>{t('adminUI.Submissions')}</TableHead>
                    <TableHead>{t('adminUI.Created')}</TableHead>
                    <TableHead>{t('adminUI.Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pdfForms.map(pdfForm => (
                    <TableRow key={pdfForm.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{pdfForm.title}</div>
                          {pdfForm.description && (
                            <div className="text-sm text-gray-500">{pdfForm.description}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={pdfForm.isActive ? 'default' : 'secondary'}>
                          {pdfForm.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewPdfSubmissions(pdfForm)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                      <TableCell>
                        {new Date(pdfForm.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => togglePdfFormActive(pdfForm.id)}
                          >
                            {pdfForm.isActive ? (
                              <ToggleRight className="w-4 h-4" />
                            ) : (
                              <ToggleLeft className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deletePdfForm(pdfForm.id, pdfForm.title)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Course Management */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('adminUI.Courses')}</CardTitle>
                <CardDescription>{t('adminUI.ViewAllSafetyTrainingCourses')}</CardDescription>
              </div>
              <Button
                onClick={() => window.location.href = '/craft-management'}
                variant="outline"
                className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
              >
                <HardHat className="w-4 h-4" />
                <span>{t('adminUI.ManageCrafts')}</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminUI.Title')}</TableHead>
                  <TableHead>{t('adminUI.Category')}</TableHead>
                  <TableHead>{t('adminUI.Duration')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {courses.map(course => (
                  <TableRow key={course.id} data-testid={`course-row-${course.id}`}>
                    <TableCell className="font-medium">{course.title}</TableCell>
                    <TableCell>
                      <Badge className="bg-orange-100 text-orange-800">{course.category}</Badge>
                    </TableCell>
                    <TableCell>{course.duration} min</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Classroom Learning Section */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Classroom Learning
                </CardTitle>
                <CardDescription>{t('adminUI.RegisterStudentsAndMarkThemAsPassedForInPersonTraining')}</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowStudentDialog(true)}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add New Student
                </Button>
                <Button
                  onClick={() => setShowAddExistingUserDialog(true)}
                  variant="outline"
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Add Existing User
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {classroomStudents.length === 0 ? (
              <div className="text-center py-8">
                <GraduationCap className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">{t('adminUI.NoClassroomStudentsRegisteredYet')}</p>
                <Button
                  onClick={() => setShowStudentDialog(true)}
                  variant="outline"
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  Add Your First Student
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {classroomStudents.map((student) => (
                  <div key={student.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{student.name}</h4>
                        <p className="text-slate-600">{student.email}</p>
                        {student.phone && <p className="text-sm text-slate-500">Phone: {student.phone}</p>}
                        {student.position && <p className="text-sm text-slate-500">Position: {student.position}</p>}
                        
                        {/* Certificates */}
                        {student.certificates && student.certificates.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-green-600 mb-2">{t('adminUI.CertificatesEarned')}</p>
                            <div className="space-y-2">
                              {student.certificates.map((cert) => (
                                <div key={cert.id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-2">
                                  <span className="inline-flex items-center text-sm font-medium text-green-800">
                                    <Award className="w-4 h-4 mr-2" />
                                    {cert.courseName}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={async () => {
                                      try {
                                        const response = await axiosInstance.get(`/certificates/${cert.id}/download`, {
                                          responseType: 'blob'
                                        });
                                        
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', `${cert.courseName.replace(/[^a-z0-9]/gi, '_')}_Certificate.txt`);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        window.URL.revokeObjectURL(url);
                                        
                                        toast.success(t('adminToasts.CertificateDownloadedSuccessfully'));
                                      } catch (error) {
                                        console.error('Failed to download certificate:', error);
                                        toast.error(t('adminToasts.FailedToDownloadCertificate'));
                                      }
                                    }}
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                  >
                                    <Download className="w-3 h-3 mr-1" />
                                    Download
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => {
                          setSelectedStudent(student);
                          setShowPassDialog(true);
                        }}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark Passed
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Student Dialog */}
        <Dialog open={showStudentDialog} onOpenChange={setShowStudentDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('adminUI.AddClassroomStudent')}</DialogTitle>
              <DialogDescription>{t('adminUI.RegisterANewStudentForInPersonTraining')}</DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleAddStudent} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="student-name">{t('adminUI.FullName')}</Label>
                <Input
                  id="student-name"
                  placeholder="John Doe"
                  value={studentForm.name}
                  onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="student-email">{t('adminUI.EmailAddress')}</Label>
                <Input
                  id="student-email"
                  type="email"
                  placeholder="john@example.com"
                  value={studentForm.email}
                  onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="student-phone">{t('adminUI.PhoneNumber')}</Label>
                <Input
                  id="student-phone"
                  placeholder="(555) 123-4567"
                  value={studentForm.phone}
                  onChange={(e) => setStudentForm({ ...studentForm, phone: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="student-position">{t('adminUI.PositionRole')}</Label>
                <Input
                  id="student-position"
                  placeholder="Construction Worker"
                  value={studentForm.position}
                  onChange={(e) => setStudentForm({ ...studentForm, position: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="student-craft">{t('adminUI.CraftTrade')}</Label>
                <Select
                  value={studentForm.craft}
                  onValueChange={(value) => setStudentForm({ ...studentForm, craft: value })}
                >
                  <SelectTrigger id="student-craft">
                    <SelectValue placeholder="Select student's craft" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {crafts.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No crafts available
                      </div>
                    ) : (
                      crafts
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((craft) => (
                          <SelectItem key={craft.id} value={craft.name}>
                            {craft.name}
                          </SelectItem>
                        ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">{t('adminUI.StudentSTrainingWillBeCustomizedBasedOnTheirCraft')}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="student-password">{t('adminUI.Password')}</Label>
                <Input
                  id="student-password"
                  type="password"
                  placeholder="Create a password for login"
                  value={studentForm.password}
                  onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
                  required
                  minLength={6}
                />
                <p className="text-xs text-slate-500">{t('adminUI.StudentWillUseThisPasswordToLogInToTheirAccount')}</p>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowStudentDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{t('adminUI.AddStudent')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>


        {/* Add Existing User to Classroom Dialog */}
        <Dialog open={showAddExistingUserDialog} onOpenChange={setShowAddExistingUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.AddExistingUserToClassroom')}</DialogTitle>
              <DialogDescription>{t('adminUI.SelectARegisteredUserFromYourCompanyToAddToClassroomTraining')}</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="existing-user">{t('adminUI.SelectUser')}</Label>
                <Select
                  value={selectedExistingUser}
                  onValueChange={(value) => setSelectedExistingUser(value)}
                >
                  <SelectTrigger id="existing-user">
                    <SelectValue placeholder="Choose a registered user" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {users.filter(u => 
                      (u.role === 'worker' || u.role === 'learner') && 
                      !classroomStudents.some(s => s.email === u.email)
                    ).length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No available users to add
                      </div>
                    ) : (
                      users
                        .filter(u => 
                          (u.role === 'worker' || u.role === 'learner') && 
                          !classroomStudents.some(s => s.email === u.email)
                        )
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name} ({user.email}) {user.craft && `- ${user.craft}`}
                          </SelectItem>
                        ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">{t('adminUI.OnlyWorkersAndLearnersWhoArenTAlreadyInTheClassroomAreShown')}</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddExistingUserDialog(false);
                  setSelectedExistingUser(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddExistingUser}
                disabled={!selectedExistingUser}
                className="bg-blue-600 hover:bg-blue-700"
              >{t('adminUI.AddToClassroom')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark Passed Dialog */}
        <Dialog open={showPassDialog} onOpenChange={setShowPassDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('adminUI.MarkStudentAsPassed')}</DialogTitle>
              <DialogDescription>
                {selectedStudent && `Generate certificate for ${selectedStudent.name}`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('adminUI.SelectCourse')}</Label>
                <select 
                  className="w-full p-2 border border-slate-300 rounded-md"
                  value={selectedCourse?.id || ''}
                  onChange={(e) => {
                    const course = courses.find(c => c.id === e.target.value);
                    setSelectedCourse(course);
                  }}
                >
                  <option value="">Choose a course...</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedCourse && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Course:</strong> {selectedCourse.title}<br />
                    <strong>Duration:</strong> {selectedCourse.duration} minutes<br />
                    <strong>Category:</strong> {selectedCourse.category}
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowPassDialog(false);
                  setSelectedStudent(null);
                  setSelectedCourse(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleMarkPassed}
                className="bg-green-600 hover:bg-green-700"
                disabled={!selectedCourse}
              >
                <Award className="w-4 h-4 mr-2" />
                Generate Certificate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
 

        {/* License Selection Dialog - Hidden on native apps per App Store guidelines */}
        {!isNativeApp && (
        <Dialog open={showLicenseDialog} onOpenChange={setShowLicenseDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('adminUI.PurchaseEmployeeLicenses')}</DialogTitle>
              <DialogDescription>{t('adminUI.SelectHowManyEmployeeLicensesYouWantToPurchaseAnnually')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <div>
                <Label htmlFor="license-count">Number of Licenses * (1-500)</Label>
                <Input
                  id="license-count"
                  type="number"
                  min="1"
                  max="500"
                  placeholder="Enter number of licenses (1-500)"
                  value={licenseCount}
                  onChange={handleLicenseCountChange}
                  className="mt-1 text-lg font-semibold"
                />
                <p className="text-xs text-slate-500 mt-1">
                  You currently have {(users || []).filter(u => u.isActive).length} active employees. You can purchase up to 500 licenses.
                </p>
              </div>

              {/* Discount Code Input */}
              <div className="space-y-2">
                <Label htmlFor="discount-code">Discount Code (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    id="discount-code"
                    placeholder="Enter discount code"
                    value={discountCode}
                    onChange={handleDiscountCodeChange}
                    className="flex-1"
                  />
                  {validatingDiscount && (
                    <div className="flex items-center px-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                    </div>
                  )}
                </div>
                {appliedDiscount && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {appliedDiscount.code} applied - {appliedDiscount.percentage}% off
                  </p>
                )}
              </div>

              {calculatedPricing && calculatedPricing.count > 0 && (
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-slate-800">{t('adminUI.PricingSummary')}</h4>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">{t('adminUI.NumberOfLicenses')}</span>
                      <span className="font-semibold">{calculatedPricing.count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">{t('adminUI.Tier')}</span>
                      <span className="font-semibold">{calculatedPricing.tier}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">{t('adminUI.PricePerLearner')}</span>
                      <span className="font-semibold text-green-600">
                        ${calculatedPricing.pricePerLearner}/year
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">{t('adminUI.Subtotal')}</span>
                      <span className="font-semibold">
                        ${calculatedPricing.subtotal.toFixed(2)}
                      </span>
                    </div>
                    {calculatedPricing.discount && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount ({calculatedPricing.discount.code} - {calculatedPricing.discount.percentage}%):</span>
                        <span className="font-semibold">
                          -${calculatedPricing.discountAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {/* Tax removed */}
                    <div className="border-t border-orange-300 pt-2 mt-2">
                      <div className="flex justify-between text-lg">
                        <span className="font-bold text-slate-800">{t('adminUI.TotalAnnualCost')}</span>
                        <span className="font-bold text-orange-600">
                          ${calculatedPricing.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Tiered Pricing:</strong>
                </p>
                <ul className="text-xs text-blue-700 mt-2 space-y-1">
                  <li>• 1-25 learners: $100/learner/year</li>
                  <li>• 26-100 learners: $90/learner/year</li>
                  <li>• 101-250 learners: $80/learner/year</li>
                  <li>• 251+ learners: $70/learner/year</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowLicenseDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleProceedToPayment}
                className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
                disabled={!licenseCount || parseInt(licenseCount) < 1}
              >{t('adminUI.ProceedToPayment')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}

        {/* Transfer Worker Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.TransferWorker')}</DialogTitle>
              <DialogDescription>{t('adminUI.TransferThisWorkerToAnotherCompany')}</DialogDescription>
            </DialogHeader>

            {transferWorker && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">{t('adminUI.WorkerDetails')}</h4>
                  <p className="text-sm"><strong>Name:</strong> {transferWorker.name}</p>
                  <p className="text-sm"><strong>Email:</strong> {transferWorker.email}</p>
                  <p className="text-sm"><strong>Role:</strong> {transferWorker.role}</p>
                </div>

                <div>
                  <Label htmlFor="destination-company-code">{t('adminUI.DestinationCompanyCode')}</Label>
                  <Input
                    id="destination-company-code"
                    placeholder="e.g., ABC123"
                    value={destinationCompanyCode}
                    onChange={(e) => setDestinationCompanyCode(e.target.value.toUpperCase())}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">{t('adminUI.EnterTheCompanyCodeWhereYouWantToTransferThisWorker')}</p>
                </div>

                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm font-semibold mb-1">{t('adminUI.WhatGetsTransferred')}</p>
                  <ul className="text-xs space-y-1">
                    <li>✅ All completed courses</li>
                    <li>✅ All certificates earned</li>
                    <li>✅ All training progress</li>
                    <li>✅ Course completion history</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-xs">
                    <strong>Note:</strong> This action is immediate and cannot be undone. 
                    Your company will lose 1 employee and the destination company will gain 1 employee.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowTransferDialog(false);
                  setTransferWorker(null);
                  setDestinationCompanyCode('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleTransferWorker}
                className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
              >{t('adminUI.TransferWorker')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Form Dialog */}
        <Dialog open={showCreateFormDialog} onOpenChange={setShowCreateFormDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.CreateNewForm')}</DialogTitle>
              <DialogDescription>{t('adminUI.CreateACustomFormToCollectInformationFromYourEmployees')}</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="form-title">{t('adminUI.FormTitle')}</Label>
                  <Input
                    id="form-title"
                    placeholder="e.g., Safety Incident Report"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label htmlFor="form-description">Description (optional)</Label>
                  <Textarea
                    id="form-description"
                    placeholder="Brief description of what this form is for..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                {/* Company Selection for Super Admin */}
                {user.role === 'super_admin' && (
                  <div>
                    <Label htmlFor="form-company">{t('adminUI.Company')}</Label>
                    <Select
                      value={formData.companyId}
                      onValueChange={(value) => setFormData({ ...formData, companyId: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a company for this form" />
                      </SelectTrigger>
                      <SelectContent>
                        {allCompanies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name} ({company.companyCode})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">{t('adminUI.ThisFormWillOnlyBeAvailableToEmployeesOfTheSelectedCompany')}</p>
                  </div>
                )}
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">{t('adminUI.FormFields')}</Label>
                  <span className="text-sm text-gray-500">{formData.fields.length} fields</span>
                </div>
                
                {/* Existing Fields */}
                {formData.fields.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {formData.fields.map((field, index) => (
                      <div key={field.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium">{field.label}</div>
                          <div className="text-sm text-gray-500">
                            {field.type} {field.required && '(required)'}
                            {field.options && field.options.length > 0 && (
                              <span> - Options: {field.options.join(', ')}</span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeFormField(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Field */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium">{t('adminUI.AddNewField')}</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <Input
                        placeholder="Field label (e.g., Employee Name)"
                        value={newField.label}
                        onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                      />
                    </div>
                    <div>
                      <Select
                        value={newField.type}
                        onValueChange={(value) => setNewField({ ...newField, type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text Input</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="textarea">Long Text</SelectItem>
                          <SelectItem value="select">Dropdown</SelectItem>
                          <SelectItem value="radio">Radio Buttons</SelectItem>
                          <SelectItem value="checkbox">Checkboxes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {(newField.type === 'select' || newField.type === 'radio' || newField.type === 'checkbox') && (
                    <div className="mt-2">
                      <Input
                        placeholder="Options (comma-separated, e.g.: Option 1, Option 2, Option 3)"
                        value={newField.options.join(', ')}
                        onChange={(e) => setNewField({ 
                          ...newField, 
                          options: e.target.value.split(',').map(opt => opt.trim()).filter(opt => opt) 
                        })}
                      />
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={newField.required}
                        onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                      />
                      <span className="text-sm">{t('adminUI.RequiredField')}</span>
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addFormField}
                      disabled={!newField.label}
                    >{t('adminUI.AddField')}</Button>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowCreateFormDialog(false);
                setFormData({ title: '', description: '', fields: [], companyId: '' });
                setNewField({ label: '', type: 'text', required: false, options: [] });
              }}>
                Cancel
              </Button>
              <Button onClick={createForm} disabled={!formData.title || formData.fields.length === 0}>
                <FileText className="w-4 h-4 mr-2" />
                Create Form
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Form Submissions Dialog */}
        <Dialog open={showFormSubmissionsDialog} onOpenChange={setShowFormSubmissionsDialog}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.FormSubmissions')}</DialogTitle>
              <DialogDescription>
                {selectedForm && (
                  <>
                    Submissions for "{selectedForm.title}" ({formSubmissions.length} total)
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {formSubmissions.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">{t('adminUI.NoSubmissionsYet')}</p>
                  <p className="text-sm text-gray-400">{t('adminUI.SubmissionsWillAppearHereWhenEmployeesFillOutTheForm')}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {formSubmissions.map((submission) => (
                    <div key={submission.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold">{submission.submitterName}</h4>
                          <p className="text-sm text-gray-500">{submission.submitterEmail}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">
                            Submitted: {new Date(submission.submittedAt).toLocaleDateString()} at{' '}
                            {new Date(submission.submittedAt).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedForm && selectedForm.fields.map((field) => (
                          <div key={field.id} className="bg-gray-50 p-3 rounded">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {field.label}
                            </label>
                            <div className="text-sm">
                              {submission.responses[field.id] || <em className="text-gray-400">No response</em>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFormSubmissionsDialog(false)}>
                Close
              </Button>
              {formSubmissions.length > 0 && selectedForm && (
                <Button onClick={() => downloadFormSubmissions(selectedForm.id, selectedForm.title)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Corrupted dialog removed - proper Add Student Dialog exists above */}

        {/* Form Submissions Dialog */}
        <Dialog open={showFormSubmissionsDialog} onOpenChange={setShowFormSubmissionsDialog}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.FormSubmissions')}</DialogTitle>
              <DialogDescription>
                {selectedForm && `Viewing submissions for "${selectedForm.title}"`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {formSubmissions.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">{t('adminUI.NoSubmissionsYet')}</p>
                  <p className="text-sm text-gray-400">{t('adminUI.SubmissionsWillAppearHereOnceEmployeesFillOutThisForm')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">
                    {formSubmissions.length} submission{formSubmissions.length !== 1 ? 's' : ''}
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {formSubmissions.map((submission, index) => (
                      <div key={submission.id} className="border rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="font-medium">Submission #{index + 1}</div>
                          <div className="text-sm text-gray-500">
                            {new Date(submission.submittedAt).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(submission.data).map(([fieldName, value]) => (
                            <div key={fieldName}>
                              <div className="text-sm font-medium text-gray-700">{fieldName}</div>
                              <div className="text-sm text-gray-900">
                                {Array.isArray(value) ? value.join(', ') : value || 'No response'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFormSubmissionsDialog(false)}>
                Close
              </Button>
              {formSubmissions.length > 0 && selectedForm && (
                <Button onClick={() => downloadFormSubmissions(selectedForm.id, selectedForm.title)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create PDF Form Dialog */}
        <Dialog open={showCreatePdfFormDialog} onOpenChange={setShowCreatePdfFormDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.AddPdfForm')}</DialogTitle>
              <DialogDescription>
                Add a fillable PDF form like W-4, I-9, or other documents for employees to complete
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="pdf-title">{t('adminUI.FormTitle')}</Label>
                <Input
                  id="pdf-title"
                  placeholder="e.g., W-4 Employee's Withholding Certificate"
                  value={pdfFormData.title}
                  onChange={(e) => setPdfFormData({ ...pdfFormData, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pdf-description">Description (optional)</Label>
                <Textarea
                  id="pdf-description"
                  placeholder="Brief description of what this form is for..."
                  value={pdfFormData.description}
                  onChange={(e) => setPdfFormData({ ...pdfFormData, description: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pdf-file">{t('adminUI.PdfTemplateFile')}</Label>
                <Input
                  id="pdf-file"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => setSelectedPdfFile(e.target.files[0])}
                  className="cursor-pointer"
                />
                {selectedPdfFile && (
                  <div className="text-sm text-green-600 bg-green-50 border border-green-200 rounded p-2">
                    Selected: {selectedPdfFile.name} ({(selectedPdfFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
                <p className="text-xs text-slate-500">{t('adminUI.UploadThePdfTemplateThatEmployeesWillDownloadAndFillOut')}</p>
              </div>

              {/* Company Selection for Super Admin */}
              {user.role === 'super_admin' && (
                <div className="space-y-2">
                  <Label htmlFor="pdf-company">{t('adminUI.Company')}</Label>
                  <Select
                    value={pdfFormData.companyId}
                    onValueChange={(value) => setPdfFormData({ ...pdfFormData, companyId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a company for this form" />
                    </SelectTrigger>
                    <SelectContent>
                      {allCompanies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name} ({company.companyCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">{t('adminUI.ThisPdfFormWillOnlyBeAvailableToEmployeesOfTheSelectedCompan')}</p>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">{t('adminUI.ReadyForPopularForms')}</p>
                    <p className="text-xs text-blue-700 mt-1">
                      System includes templates for W-4, I-9, and other common HR forms that employees can fill out digitally.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowCreatePdfFormDialog(false);
                setPdfFormData({ title: '', description: '', companyId: '' });
                setSelectedPdfFile(null);
              }}>
                Cancel
              </Button>
              <Button onClick={createPdfForm}>
                <FileText className="w-4 h-4 mr-2" />
                Add PDF Form
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PDF Form Submissions Dialog */}
        <Dialog open={showPdfSubmissionsDialog} onOpenChange={(open) => {
          setShowPdfSubmissionsDialog(open);
          if (!open) {
            // Clear submissions and form when closing to ensure clean state
            setPdfSubmissions([]);
            setSelectedPdfForm(null);
          }
        }}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.PdfFormSubmissions')}</DialogTitle>
              <DialogDescription>
                {selectedPdfForm && (
                  <>
                    Submissions for "{selectedPdfForm.title}" ({pdfSubmissions.length} total)
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {pdfSubmissions.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">{t('adminUI.NoSubmissionsYet')}</p>
                  <p className="text-sm text-gray-400">{t('adminUI.SubmissionsWillAppearHereWhenEmployeesFillOutThePdfForm')}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {pdfSubmissions.map((submission) => (
                    <div key={submission.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold">{submission.submitterName}</h4>
                          <p className="text-sm text-gray-500">{submission.submitterEmail}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">
                            Submitted: {new Date(submission.submittedAt).toLocaleDateString()} at{' '}
                            {new Date(submission.submittedAt).toLocaleTimeString()}
                          </p>
                          {submission.filledPdfPath ? (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => downloadCompletedPdf(
                                selectedPdfForm.id, 
                                submission.id, 
                                submission.submitterName, 
                                selectedPdfForm.title
                              )}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download PDF
                            </Button>
                          ) : (
                            <div className="text-sm text-gray-500 mt-2">
                              No PDF file uploaded
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(submission.responses || {}).map(([fieldName, value]) => (
                          <div key={fieldName} className="bg-gray-50 p-3 rounded">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {fieldName}
                            </label>
                            <div className="text-sm">
                              {value || <em className="text-gray-400">No response</em>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPdfSubmissionsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Toolbox Talks Management - Phase 3: Add Create Buttons */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <ClipboardList className="w-5 h-5 mr-2 text-orange-600" />
                  Toolbox Talks
                </CardTitle>
                <CardDescription>{t('adminUI.CreateAndManageToolboxTalksForSupervisors')}</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const response = await axiosInstance.get('/toolbox-talk-sessions');
                      setToolboxSessions(response.data);
                      setShowToolboxSessionsDialog(true);
                    } catch (error) {
                      console.error('Failed to load sessions:', error);
                      toast.error(t('adminToasts.FailedToLoadSessions'));
                    }
                  }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Sessions
                </Button>
                <Button
                  onClick={() => setShowCreateTalkDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Text Talk
                </Button>
                <Button
                  onClick={() => setShowUploadTalkDialog(true)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  Upload PDF Talk
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-600 mb-4">
              <strong>Status:</strong> {toolboxTalks.length} toolbox talk{toolboxTalks.length !== 1 ? 's' : ''} loaded
            </div>
            {toolboxTalks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('adminUI.NoToolboxTalksCreatedYet')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 mb-2">{t('adminUI.AvailableTalks')}</p>
                {toolboxTalks.map((talk) => (
                  <div key={talk.id} className="p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-900 break-words">{talk.title}</p>
                          {talk.pdfPath && (
                            <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
                          )}
                          {talk.category && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded flex-shrink-0">
                              {talk.category}
                            </span>
                          )}
                        </div>
                        {talk.description && (
                          <p className="text-sm text-gray-500 mt-1 break-words">{talk.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewToolboxTalk(talk)}
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {talk.pdfPath && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadToolboxTalkPdf(talk)}
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteToolboxTalk(talk.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Delete talk"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Toolbox Talks Management - DISABLED (Full Feature) */}
        {false && <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <ClipboardList className="w-5 h-5 mr-2 text-orange-600" />
                  Toolbox Talks
                </CardTitle>
                <CardDescription>{t('adminUI.CreateAndManageToolboxTalksForSupervisors')}</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowCreateTalkDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Text Talk
                </Button>
                <Button
                  onClick={() => setShowUploadTalkDialog(true)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  Upload PDF Talk
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {toolboxTalks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ClipboardList className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>{t('adminUI.NoToolboxTalksCreatedYet')}</p>
                <p className="text-sm">{t('adminUI.CreateTextBasedTalksOrUploadPdfFilesForSupervisorsToUse')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {toolboxTalks.map((talk) => (
                  <Card key={talk.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{talk.title}</CardTitle>
                          {talk.category && (
                            <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                              {talk.category}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {talk.pdfPath ? (
                            <FileText className="w-5 h-5 text-red-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {talk.description || 'No description'}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <span>Type: {talk.pdfPath ? 'PDF Document' : 'Text Content'}</span>
                        <span>{new Date(talk.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-2">
                        {talk.pdfPath && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadAuthed(`/toolbox-talks/${talk.id}/download`, `${talk.title || 'toolbox-talk'}.pdf`)}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteToolboxTalk(talk.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>}


        {/* Users Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('admin.allUsers')}</CardTitle>
                <CardDescription>{t('admin.allUsersDesc')}</CardDescription>
              </div>
              <div className="flex gap-3 items-center">
                <Button
                  onClick={() => setShowCreateUserDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create User
                </Button>
                <Button
                  onClick={() => setShowReminderDialog(true)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Send Training Reminder
                </Button>
                <Button
                  onClick={() => setShowMessageDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Message to Workers
                </Button>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full pl-9"
                  />
                  {userSearchQuery && (
                    <button
                      onClick={() => setUserSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 && userSearchQuery ? (
              <div className="text-center py-8 text-slate-500">
                <p>No users found matching "{userSearchQuery}"</p>
                <Button
                  variant="link"
                  onClick={() => setUserSearchQuery('')}
                  className="mt-2"
                >
                  Clear search
                </Button>
              </div>
            ) : (
              <div>
                <div className="text-sm text-slate-600 mb-4">
                  Showing {filteredUsers.length} of {users.length} users
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>{t('adminUI.Email')}</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>{t('adminUI.Company')}</TableHead>
                      {user.role === 'company_admin' && <TableHead>{t('adminUI.Actions')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                {filteredUsers.map(u => (
                  <TableRow 
                    key={u.id} 
                    data-testid={`user-row-${u.id}`}
                    className={!u.isActive ? 'opacity-50 bg-slate-50' : ''}
                  >
                    <TableCell className="font-medium">
                      {u.name}
                      {!u.isActive && (
                        <Badge variant="outline" className="ml-2 text-xs border-red-200 text-red-600">{t('adminUI.Inactive')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge
                        className={
                          u.role === 'company_admin'
                            ? 'bg-purple-100 text-purple-800'
                            : u.role === 'supervisor'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }
                      >
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>{analytics.company?.name || 'N/A'}</TableCell>
                    {user.role === 'company_admin' && (
                      <TableCell>
                        {u.role === 'worker' || u.role === 'supervisor' ? (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditUserDialog(u)}
                            >
                              Edit
                            </Button>
                            {user?.role === 'super_admin' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setTransferWorker(u);
                                  setShowTransferDialog(true);
                                }}
                                disabled={!u.isActive}
                              >
                                Transfer
                              </Button>
                            )}
                            <Button
                              variant={u.isActive ? "destructive" : "default"}
                              size="sm"
                              onClick={() => handleToggleWorkerStatus(u)}
                            >
                              {u.isActive ? 'Deactivate' : 'Reactivate'}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">N/A</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Craft Management Dialog */}

        {/* Send Message Dialog */}
        <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.SendMessageToWorkers')}</DialogTitle>
              <DialogDescription>{t('adminUI.SendTrainingRemindersOrImportantNotifications')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('adminUI.Recipients')}</Label>
                <Select
                  value={messageForm.recipients}
                  onValueChange={(value) => setMessageForm({ ...messageForm, recipients: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workers/Learners</SelectItem>
                    <SelectItem value="incomplete">Workers with Incomplete Training</SelectItem>
                    <SelectItem value="specific">Specific Users (Select Below)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {messageForm.recipients === 'specific' && (
                <div className="space-y-2">
                  <Label>{t('adminUI.SelectUsers')}</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-2">
                    {users.filter(u => u.role === 'worker' || u.role === 'learner').map(u => (
                      <div key={u.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(u.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers([...selectedUsers, u.id]);
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== u.id));
                            }
                          }}
                        />
                        <span className="text-sm">{u.name} ({u.email})</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">{selectedUsers.length} user(s) selected</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="message-subject">{t('adminUI.Subject')}</Label>
                <Input
                  id="message-subject"
                  placeholder="e.g., Training Reminder: Complete Safety Course"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message-body">{t('adminUI.Message')}</Label>
                <Textarea
                  id="message-body"
                  placeholder="Enter your message here..."
                  rows={6}
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMessageDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={sendMessageToWorkers}
                disabled={!messageForm.subject || !messageForm.message || (messageForm.recipients === 'specific' && selectedUsers.length === 0)}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Training Reminder Dialog */}
        <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.SendTrainingReminder')}</DialogTitle>
              <DialogDescription>{t('adminUI.SendEmailRemindersToWorkersAboutCompletingTheirTraining')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('adminUI.WhoShouldReceiveThisReminder')}</Label>
                <Select
                  value={reminderForm.recipientType}
                  onValueChange={(value) => setReminderForm({ ...reminderForm, recipientType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workers</SelectItem>
                    <SelectItem value="not_started">Workers Who Haven't Started Training</SelectItem>
                    <SelectItem value="incomplete">Workers with Incomplete Courses</SelectItem>
                    <SelectItem value="specific">Specific Workers (Select Below)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {reminderForm.recipientType === 'specific' && (
                <div className="space-y-2">
                  <Label>{t('adminUI.SelectWorkers')}</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-2">
                    {users.filter(u => u.role === 'worker' || u.role === 'learner').map(u => (
                      <div key={u.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={reminderForm.userIds.includes(u.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setReminderForm({ ...reminderForm, userIds: [...reminderForm.userIds, u.id] });
                            } else {
                              setReminderForm({ ...reminderForm, userIds: reminderForm.userIds.filter(id => id !== u.id) });
                            }
                          }}
                        />
                        <span className="text-sm">{u.name} ({u.email})</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">{reminderForm.userIds.length} worker(s) selected</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('adminUI.MessageTemplate')}</Label>
                <Select
                  value={reminderForm.messageType}
                  onValueChange={(value) => setReminderForm({ ...reminderForm, messageType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Reminder</SelectItem>
                    <SelectItem value="not_started">Not Started Reminder</SelectItem>
                    <SelectItem value="incomplete">Incomplete Training Reminder</SelectItem>
                    <SelectItem value="custom">Custom Message</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {reminderForm.messageType === 'general' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Preview:</strong> "This is a reminder to complete your required safety training. Please ensure all your assigned courses are completed."
                  </p>
                </div>
              )}

              {reminderForm.messageType === 'not_started' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-orange-900">
                    <strong>Preview:</strong> "We noticed you haven't started your required safety training yet. Please log in and begin your training as soon as possible."
                  </p>
                </div>
              )}

              {reminderForm.messageType === 'incomplete' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-900">
                    <strong>Preview:</strong> "We noticed you have training courses that are in progress but not yet completed. Please complete your pending courses at your earliest convenience."
                  </p>
                </div>
              )}

              {reminderForm.messageType === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="custom-reminder-message">{t('adminUI.CustomMessage')}</Label>
                  <Textarea
                    id="custom-reminder-message"
                    placeholder="Write your custom reminder message here..."
                    rows={6}
                    value={reminderForm.customMessage}
                    onChange={(e) => setReminderForm({ ...reminderForm, customMessage: e.target.value })}
                    required
                  />
                  <p className="text-xs text-gray-500">
                    This message will be included in the email along with worker's incomplete courses (if applicable).
                  </p>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900">
                  <strong>📧 Email Preview:</strong>
                </p>
                <ul className="text-xs text-amber-800 mt-2 space-y-1">
                  <li>✓ Personalized with worker's name</li>
                  <li>✓ Lists incomplete courses with progress</li>
                  <li>✓ Includes "Access Training Dashboard" button</li>
                  <li>✓ Branded with your company name</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> SMS reminders require Twilio configuration. Contact your admin to enable SMS notifications.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReminderDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={sendTrainingReminders}
                disabled={
                  sendingReminders ||
                  (reminderForm.messageType === 'custom' && !reminderForm.customMessage) ||
                  (reminderForm.recipientType === 'specific' && reminderForm.userIds.length === 0)
                }
                className="bg-orange-600 hover:bg-orange-700"
              >
                {sendingReminders ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email Reminders
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Create Toolbox Talk Dialog */}
        <Dialog open={showCreateTalkDialog} onOpenChange={setShowCreateTalkDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.CreateTextBasedToolboxTalk')}</DialogTitle>
              <DialogDescription>{t('adminUI.CreateAToolboxTalkWithTextContentForSupervisorsToRead')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="talk-title">{t('adminUI.Title')}</Label>
                <Input
                  id="talk-title"
                  placeholder="e.g., Ladder Safety, PPE Requirements"
                  value={talkForm.title}
                  onChange={(e) => setTalkForm({ ...talkForm, title: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="talk-category">{t('adminUI.Category')}</Label>
                <Input
                  id="talk-category"
                  placeholder="e.g., Safety, Health, Equipment"
                  value={talkForm.category}
                  onChange={(e) => setTalkForm({ ...talkForm, category: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="talk-description">{t('adminUI.Description')}</Label>
                <Textarea
                  id="talk-description"
                  placeholder="Brief description of the toolbox talk"
                  rows={2}
                  value={talkForm.description}
                  onChange={(e) => setTalkForm({ ...talkForm, description: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="talk-content">{t('adminUI.Content')}</Label>
                <Textarea
                  id="talk-content"
                  placeholder="Enter the full content of the toolbox talk that supervisors will read to workers..."
                  rows={10}
                  value={talkForm.content}
                  onChange={(e) => setTalkForm({ ...talkForm, content: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500">{t('adminUI.ThisContentWillBeDisplayedToSupervisorsWhenConductingTheTool')}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateTalkDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={createToolboxTalk}
                disabled={!talkForm.title || !talkForm.content || uploadingTalk}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {uploadingTalk ? 'Creating...' : 'Create Toolbox Talk'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload Toolbox Talk PDF Dialog */}
        <Dialog open={showUploadTalkDialog} onOpenChange={setShowUploadTalkDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.UploadPdfToolboxTalk')}</DialogTitle>
              <DialogDescription>{t('adminUI.UploadAPdfFileContainingAToolboxTalkForSupervisors')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="pdf-talk-title">{t('adminUI.Title')}</Label>
                <Input
                  id="pdf-talk-title"
                  placeholder="e.g., Fall Protection Guidelines"
                  value={talkForm.title}
                  onChange={(e) => setTalkForm({ ...talkForm, title: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pdf-talk-category">{t('adminUI.Category')}</Label>
                <Input
                  id="pdf-talk-category"
                  placeholder="e.g., Safety, Health, Equipment"
                  value={talkForm.category}
                  onChange={(e) => setTalkForm({ ...talkForm, category: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pdf-talk-description">{t('adminUI.Description')}</Label>
                <Textarea
                  id="pdf-talk-description"
                  placeholder="Brief description of the toolbox talk"
                  rows={2}
                  value={talkForm.description}
                  onChange={(e) => setTalkForm({ ...talkForm, description: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pdf-file">{t('adminUI.PdfFile')}</Label>
                <Input
                  id="pdf-file"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setSelectedTalkPdf(e.target.files[0])}
                  required
                />
                {selectedTalkPdf && (
                  <div className="text-sm text-green-600 flex items-center gap-2 mt-2">
                    <FileText className="w-4 h-4" />
                    {selectedTalkPdf.name} ({(selectedTalkPdf.size / 1024).toFixed(2)} KB)
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> Supervisors will be able to download and view this PDF when conducting the toolbox talk.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowUploadTalkDialog(false);
                setSelectedTalkPdf(null);
              }}>
                Cancel
              </Button>
              <Button
                onClick={uploadToolboxTalkPdf}
                disabled={!talkForm.title || !selectedTalkPdf || uploadingTalk}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {uploadingTalk ? 'Uploading...' : 'Upload PDF'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Toolbox Talk Dialog */}
        <Dialog open={showViewTalkDialog} onOpenChange={setShowViewTalkDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedTalk?.pdfPath && <FileText className="w-5 h-5 text-red-600" />}
                {selectedTalk?.title}
              </DialogTitle>
              <DialogDescription>
                {selectedTalk?.category && (
                  <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded mr-2">
                    {selectedTalk.category}
                  </span>
                )}
                {selectedTalk?.description}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {selectedTalk?.pdfPath ? (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <FileText className="w-16 h-16 mx-auto text-red-600 mb-3" />
                    <p className="text-sm text-gray-600 mb-3">{t('adminUI.ThisIsAPdfBasedToolboxTalk')}</p>
                    <Button
                      onClick={() => downloadToolboxTalkPdf(selectedTalk)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('adminUI.Content')}</h4>
                    <div className="p-4 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm text-gray-700">
                      {selectedTalk?.content}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewTalkDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Toolbox Talk Sessions Dialog */}
        <Dialog open={showToolboxSessionsDialog} onOpenChange={setShowToolboxSessionsDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.ToolboxTalkSessions')}</DialogTitle>
              <DialogDescription>{t('adminUI.ViewAllCompletedToolboxTalkSessionsBySupervisors')}</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {toolboxSessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('adminUI.NoToolboxTalkSessionsRecordedYet')}</p>
                  <p className="text-sm">{t('adminUI.SessionsWillAppearHereWhenSupervisorsCompleteToolboxTalks')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('adminUI.TalkTitle')}</TableHead>
                      <TableHead>{t('adminUI.Supervisor')}</TableHead>
                      <TableHead>{t('adminUI.Attendees')}</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>{t('adminUI.Duration')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {toolboxSessions.map((session) => (
                      <TableRow key={session.id}>
                        <TableCell className="font-medium">{session.talkTitle}</TableCell>
                        <TableCell>{session.supervisorName}</TableCell>
                        <TableCell>{session.attendeeCount} workers</TableCell>
                        <TableCell>{new Date(session.completedAt).toLocaleDateString()}</TableCell>
                        <TableCell>{session.duration || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowToolboxSessionsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cloud Storage Configuration Dialog */}
        <Dialog open={showCloudStorageDialog} onOpenChange={setShowCloudStorageDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.ConfigureCloudStorage')}</DialogTitle>
              <DialogDescription>
                Set up your cloud storage provider to ensure uploaded files persist across deployments
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cloud-provider">{t('adminUI.CloudProvider')}</Label>
                <select
                  id="cloud-provider"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={cloudStorageForm.provider}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, provider: e.target.value })}
                >
                  <option value="aws_s3">AWS S3</option>
                  <option value="google_cloud">Google Cloud Storage</option>
                  <option value="azure_blob">Azure Blob Storage</option>
                  <option value="cloudflare_r2">Cloudflare R2</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cloud-access-key">{t('adminUI.AccessKeyClientId')}</Label>
                <Input
                  id="cloud-access-key"
                  placeholder="Your access key or client ID"
                  value={cloudStorageForm.accessKey}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, accessKey: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cloud-secret-key">{t('adminUI.SecretKeyClientSecret')}</Label>
                <Input
                  id="cloud-secret-key"
                  type="password"
                  placeholder="Your secret key (will be encrypted)"
                  value={cloudStorageForm.secretKey}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, secretKey: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500">{t('adminUI.ThisWillBeStoredSecurelyAndEncrypted')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cloud-bucket">{t('adminUI.BucketContainerName')}</Label>
                <Input
                  id="cloud-bucket"
                  placeholder="my-company-storage"
                  value={cloudStorageForm.bucketName}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, bucketName: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cloud-region">Region (Optional)</Label>
                <Input
                  id="cloud-region"
                  placeholder="e.g., us-east-1, us-central1"
                  value={cloudStorageForm.region}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, region: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cloud-endpoint">Custom Endpoint (Optional)</Label>
                <Input
                  id="cloud-endpoint"
                  placeholder="For R2, MinIO, or custom endpoints"
                  value={cloudStorageForm.customEndpoint}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, customEndpoint: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCloudStorageDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={saveCloudStorageConfig}
                disabled={savingCloudStorage}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingCloudStorage ? 'Saving...' : 'Save Configuration'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* File Manager Dialog */}
        <Dialog open={showFilesDialog} onOpenChange={(open) => {
          setShowFilesDialog(open);
          if (!open) {
            // Clear files when closing to ensure clean state
            setUploadedFiles([]);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.UploadedFiles')}</DialogTitle>
              <DialogDescription>
                Manage your company's uploaded files. Total: {uploadedFiles.length} files ({(uploadedFiles.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)} MB)
              </DialogDescription>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={async () => {
                    if (!window.confirm('Delete all missing file records? This cannot be undone.')) return;
                    try {
                      const response = await axiosInstance.post('/admin/files/delete-missing');
                      toast.success(`Deleted ${response.data.deleted} missing file records`);
                      // Reload files
                      const filesResponse = await axiosInstance.get('/admin/files');
                      const fileList = filesResponse.data.files.map(f => ({
                        ...f,
                        sizeKB: (f.size / 1024).toFixed(2)
                      }));
                      setUploadedFiles(fileList);
                    } catch (error) {
                      console.error('Failed to delete missing files:', error);
                      toast.error(t('adminToasts.FailedToDeleteMissingFiles'));
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete All Missing
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  onClick={async () => {
                    const existingFiles = uploadedFiles.filter(f => f.exists);
                    if (existingFiles.length === 0) {
                      toast.info(t('adminToasts.NoFilesAvailableToDownload'));
                      return;
                    }
                    
                    if (!window.confirm(`Download all ${existingFiles.length} files? This may take a moment.`)) return;
                    
                    try {
                      toast.info(`Downloading ${existingFiles.length} files...`);
                      let successCount = 0;
                      let failCount = 0;
                      
                      for (const file of existingFiles) {
                        try {
                          const response = await axiosInstance.get(
                            `/admin/files/download/${file.type}/${file.id}`,
                            { responseType: 'blob' }
                          );
                          
                          const blob = new Blob([response.data], {
                            type: response.headers['content-type'] || 'application/octet-stream'
                          });
                          
                          // Use FileReader to create data URL
                          await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onload = function() {
                              const a = document.createElement('a');
                              a.href = reader.result;
                              a.download = file.name;
                              a.style.display = 'none';
                              document.body.appendChild(a);
                              a.click();
                              setTimeout(() => {
                                document.body.removeChild(a);
                                resolve();
                              }, 100);
                            };
                            reader.readAsDataURL(blob);
                          });
                          
                          successCount++;
                          
                          // Small delay between downloads
                          await new Promise(resolve => setTimeout(resolve, 300));
                        } catch (error) {
                          console.error(`Failed to download ${file.name}:`, error);
                          failCount++;
                        }
                      }
                      
                      if (successCount > 0) {
                        toast.success(`Successfully downloaded ${successCount} file(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
                      } else {
                        toast.error(t('adminToasts.FailedToDownloadFiles'));
                      }
                    } catch (error) {
                      console.error('Failed to download all files:', error);
                      toast.error(t('adminToasts.FailedToDownloadFiles'));
                    }
                  }}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download All Files
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm('Migrate all local files to cloud storage? This will upload existing files to your configured R2 bucket.')) return;
                    try {
                      toast.info(t('adminToasts.MigratingFilesToCloudThisMayTakeAMoment'));
                      const response = await axiosInstance.post('/admin/files/migrate-to-cloud');
                      toast.success(response.data.message);
                      // Reload files
                      const filesResponse = await axiosInstance.get('/admin/files');
                      const fileList = filesResponse.data.files.map(f => ({
                        ...f,
                        sizeKB: (f.size / 1024).toFixed(2)
                      }));
                      setUploadedFiles(fileList);
                    } catch (error) {
                      console.error('Failed to migrate files:', error);
                      toast.error(error.response?.data?.detail || 'Failed to migrate files');
                    }
                  }}
                >
                  <CloudUpload className="w-4 h-4 mr-1" />
                  Migrate to Cloud
                </Button>
              </div>
            </DialogHeader>
            <div className="py-4">
              {uploadedFiles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('adminUI.NoFilesUploadedYet')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className={`p-3 border rounded-lg flex items-center justify-between ${!file.exists ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{file.name}</p>
                          {!file.exists && (
                            <span className="px-2 py-0.5 text-xs bg-red-600 text-white rounded">{t('adminUI.Missing')}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                            {file.type.replace(/_/g, ' ')}
                          </span>
                          <span>{file.sizeKB} KB</span>
                          {file.uploaderName && <span>by {file.uploaderName}</span>}
                          {file.uploadedAt && (
                            <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                          )}
                        </div>
                        {!file.exists && (
                          <p className="text-xs text-red-600 mt-1">⚠️ File lost during deployment. Re-upload or configure cloud storage.</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!file.exists}
                          onClick={async () => {
                            if (!file.exists) {
                              toast.error(t('adminToasts.FileIsMissing'));
                              return;
                            }
                            
                            try {
                              // Use axios to download with auth header, then trigger browser download
                              const response = await axiosInstance.get(
                                `/admin/files/download/${file.type}/${file.id}`,
                                { responseType: 'blob' }
                              );
                              
                              // Create blob and download
                              const blob = new Blob([response.data], {
                                type: response.headers['content-type'] || 'application/octet-stream'
                              });
                              
                              // For sandboxed iframes, use a different approach
                              // Create a data URL instead of blob URL
                              const reader = new FileReader();
                              reader.onload = function() {
                                const a = document.createElement('a');
                                a.href = reader.result;
                                a.download = file.name;
                                a.style.display = 'none';
                                document.body.appendChild(a);
                                a.click();
                                setTimeout(() => {
                                  document.body.removeChild(a);
                                }, 100);
                              };
                              reader.readAsDataURL(blob);
                              
                              toast.success(`Downloaded ${file.name}`);
                            } catch (error) {
                              console.error('Download error:', error);
                              toast.error(error.response?.data?.detail || 'Failed to download file');
                            }
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {file.exists ? 'Download' : 'Missing'}
                        </Button>
                        {file.type !== 'toolbox_talk_pdf' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              // Use custom confirm dialog instead of window.confirm for sandboxed iframes
                              const confirmDelete = window.confirm ? 
                                window.confirm(`Delete ${file.name}? This cannot be undone.`) :
                                true; // Auto-confirm if confirm() is blocked
                              
                              if (!confirmDelete) return;
                              
                              try {
                                await axiosInstance.delete(`/admin/files/${file.type}/${file.id}`);
                                toast.success(t('adminToasts.FileDeleted'));
                                // Remove from list
                                setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
                              } catch (error) {
                                console.error('Failed to delete file:', error);
                                toast.error(t('adminToasts.FailedToDeleteFile'));
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFilesDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Incident Report Detail Dialog */}
        <Dialog open={showIncidentReportsDialog} onOpenChange={setShowIncidentReportsDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.IncidentReportDetails')}</DialogTitle>
              <DialogDescription>{t('adminUI.ReviewAndManageIncidentReport')}</DialogDescription>
            </DialogHeader>

            {selectedIncident && (
              <div className="space-y-4 py-4">
                {/* Status and Severity */}
                <div className="flex items-center gap-3">
                  <Badge className={
                    selectedIncident.severity === 'Critical' ? 'bg-red-600 text-lg' :
                    selectedIncident.severity === 'High' ? 'bg-orange-600 text-lg' :
                    selectedIncident.severity === 'Medium' ? 'bg-yellow-600 text-lg' : 'bg-blue-600 text-lg'
                  }>
                    {selectedIncident.severity} Severity
                  </Badge>
                  <Badge variant="outline" className="text-lg">
                    {selectedIncident.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4 border-b pb-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('adminUI.IncidentType')}</p>
                    <p className="font-semibold">{selectedIncident.incidentType}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('adminUI.ReportedBy')}</p>
                    <p className="font-semibold">{selectedIncident.supervisorName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('adminUI.DateTime')}</p>
                    <p className="font-semibold">{selectedIncident.incidentDate} at {selectedIncident.incidentTime}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('adminUI.Location')}</p>
                    <p className="font-semibold">{selectedIncident.location}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-semibold mb-2">{t('adminUI.Description')}</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedIncident.description}</p>
                </div>

                {/* People Involved */}
                {selectedIncident.peopleInvolved && selectedIncident.peopleInvolved.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">{t('adminUI.PeopleInvolved')}</h3>
                    <div className="space-y-2">
                      {selectedIncident.peopleInvolved.map((person, idx) => (
                        <div key={idx} className="bg-gray-50 p-3 rounded">
                          <p><span className="font-medium">{person.name}</span> - {person.role}</p>
                          {person.details && <p className="text-sm text-gray-600">{person.details}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Equipment/Property */}
                {selectedIncident.equipmentInvolved && (
                  <div>
                    <h3 className="font-semibold mb-2">{t('adminUI.EquipmentInvolved')}</h3>
                    <p className="bg-gray-50 p-3 rounded">{selectedIncident.equipmentInvolved}</p>
                  </div>
                )}
                {selectedIncident.propertyDamaged && (
                  <div>
                    <h3 className="font-semibold mb-2">{t('adminUI.PropertyDamaged')}</h3>
                    <p className="bg-gray-50 p-3 rounded">{selectedIncident.propertyDamaged}</p>
                  </div>
                )}

                {/* Actions */}
                <div>
                  <h3 className="font-semibold mb-2">{t('adminUI.CorrectiveActionsTaken')}</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedIncident.correctiveActions}</p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">{t('adminUI.PreventiveMeasures')}</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedIncident.preventiveMeasures}</p>
                </div>

                {/* Photos */}
                {selectedIncident.photoUrls && selectedIncident.photoUrls.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">{t('adminUI.AttachedPhotos')}</h3>
                    <div className="flex gap-2 flex-wrap">
                      {selectedIncident.photoUrls.map((url, idx) => (
                        <Badge key={idx} variant="secondary">Photo {idx + 1}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked JSAs from Location */}
                {selectedIncident.linkedJSAs && selectedIncident.linkedJSAs.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      JSAs at Incident Location ({selectedIncident.linkedJSAs.length})
                    </h3>
                    <div className="space-y-3">
                      {selectedIncident.linkedJSAs.map((jsa, idx) => (
                        <div key={idx} className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-1">{jsa.task}</h4>
                          <p className="text-sm text-gray-600 mb-2">📍 {jsa.location}</p>
                          {jsa.hazards && jsa.hazards.length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium text-gray-700 mb-1">{t('adminUI.IdentifiedHazards')}</p>
                              <ul className="text-sm text-gray-600 ml-4 list-disc">
                                {jsa.hazards.map((hazard, hi) => (
                                  <li key={hi}>{hazard.hazard}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked Audits from Location */}
                {selectedIncident.linkedAudits && selectedIncident.linkedAudits.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                      Recent Audits at Location ({selectedIncident.linkedAudits.length})
                    </h3>
                    <div className="space-y-3">
                      {selectedIncident.linkedAudits.map((audit, idx) => (
                        <div key={idx} className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                Audit: {new Date(audit.auditDate).toLocaleDateString()}
                              </h4>
                              <p className="text-sm text-gray-600">📍 {audit.location}</p>
                            </div>
                            <Badge className={audit.complianceScore >= 80 ? 'bg-green-600' : audit.complianceScore >= 60 ? 'bg-yellow-600' : 'bg-red-600'}>
                              {audit.complianceScore}% Compliance
                            </Badge>
                          </div>
                          {audit.issues && audit.issues.length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium text-gray-700 mb-1">{t('adminUI.IssuesFound')}</p>
                              <ul className="text-sm text-gray-600 ml-4 list-disc">
                                {audit.issues.map((issue, ii) => (
                                  <li key={ii}>{issue.description || issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status Update Section */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-semibold mb-3">{t('adminUI.UpdateStatus')}</h3>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant={selectedIncident.status === 'submitted' ? 'default' : 'outline'}
                      onClick={() => handleUpdateIncidentStatus(selectedIncident.id, 'submitted')}
                      disabled={updatingIncidentStatus}
                    >
                      Submitted
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedIncident.status === 'under_review' ? 'default' : 'outline'}
                      onClick={() => handleUpdateIncidentStatus(selectedIncident.id, 'under_review')}
                      disabled={updatingIncidentStatus}
                    >
                      Under Review
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedIncident.status === 'resolved' ? 'default' : 'outline'}
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleUpdateIncidentStatus(selectedIncident.id, 'resolved')}
                      disabled={updatingIncidentStatus}
                    >
                      Resolved
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="flex justify-between">
              <Button 
                variant="outline" 
                className="text-red-600 hover:text-red-700 hover:border-red-600"
                onClick={() => handleDeleteIncident(selectedIncident.id, selectedIncident.incidentType)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Report
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowIncidentReportsDialog(false)}>
                  Close
                </Button>
                {selectedIncident && selectedIncident.status !== 'draft' && (
                  <Button onClick={() => handleDownloadIncidentPdf(selectedIncident.id)}>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                )}
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Toolbox Talk Sessions Dialog (Admin View) */}
        <Dialog open={showToolboxSessionsDialog} onOpenChange={setShowToolboxSessionsDialog}>
          <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>{t('adminUI.ToolboxTalkSessions')}</DialogTitle>
                  <DialogDescription>
                    View all completed toolbox talk sessions by supervisors. Total: {toolboxSessions.length} sessions
                  </DialogDescription>
                </div>
                {toolboxSessions.length > 0 && (
                  <Button
                    onClick={handleExportAllToolboxPDFs}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export All PDFs
                  </Button>
                )}
              </div>
            </DialogHeader>
            <div className="py-4">
              {toolboxSessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('adminUI.NoToolboxTalkSessionsRecordedYet')}</p>
                  <p className="text-sm">{t('adminUI.SessionsWillAppearHereWhenSupervisorsCompleteToolboxTalks')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {toolboxSessions.map((session) => {
                    const talk = toolboxTalks.find(t => t.id === session.toolboxTalkId);
                    const totalAttendees = (session.attendees?.length || 0) + (session.manualAttendees?.length || 0);
                    
                    return (
                      <div key={session.id} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{talk?.title || 'Unknown Talk'}</h4>
                            <div className="mt-2 space-y-1 text-sm text-gray-600">
                              <p><strong>Date:</strong> {new Date(session.conductedAt).toLocaleString()}</p>
                              <p><strong>Location:</strong> {session.location || 'N/A'}</p>
                              {session.duration && <p><strong>Duration:</strong> {session.duration} minutes</p>}
                              <p><strong>Attendees:</strong> {totalAttendees} workers</p>
                              {session.manualAttendees && session.manualAttendees.length > 0 && (
                                <p className="text-xs mt-1">Manual entries: {session.manualAttendees.join(', ')}</p>
                              )}
                              {session.supervisorNotes && (
                                <div className="mt-2 p-2 bg-gray-50 rounded">
                                  <p className="text-xs font-medium">{t('adminUI.SupervisorNotes')}</p>
                                  <p className="text-xs mt-1">{session.supervisorNotes}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Badge className="bg-green-100 text-green-800">{t('adminUI.Completed')}</Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const response = await axiosInstance.get(
                                    `/toolbox-talk-sessions/${session.id}/export`,
                                    { responseType: 'blob' }
                                  );
                                  
                                  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `toolbox_talk_${talk?.title?.replace(/\s+/g, '_') || 'session'}_${new Date(session.conductedAt).toLocaleDateString().replace(/\//g, '-')}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  
                                  toast.success(t('adminToasts.ToolboxTalkRecordDownloadedAsPdf'));
                                } catch (error) {
                                  console.error('Failed to export:', error);
                                  toast.error(t('adminToasts.FailedToExportPdf'));
                                }
                              }}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Export PDF
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowToolboxSessionsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Edit User Dialog */}
        <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.EditUserInformation')}</DialogTitle>
              <DialogDescription>
                Update user details for {editingUser?.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">{t('adminUI.FullName')}</Label>
                    <Input
                      id="edit-name"
                      value={editUserForm.name}
                      onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">{t('adminUI.Email')}</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editUserForm.email}
                      onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-password">New Password (optional)</Label>
                    <Input
                      id="edit-password"
                      type="password"
                      value={editUserForm.password}
                      onChange={(e) => setEditUserForm({...editUserForm, password: e.target.value})}
                      placeholder="Leave blank to keep current"
                    />
                    <p className="text-xs text-slate-500">{t('adminUI.OnlyFillThisIfYouWantToChangeThePassword')}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-phone">Phone (optional)</Label>
                    <Input
                      id="edit-phone"
                      type="tel"
                      value={editUserForm.phone}
                      onChange={(e) => setEditUserForm({...editUserForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                
                {(user.role === 'super_admin' || user.role === 'admin' || user.role === 'company_admin') && (
                  <div className="space-y-2">
                    <Label htmlFor="edit-role">{t('common.role')}</Label>
                    <Select
                      value={editUserForm.role}
                      onValueChange={(value) => setEditUserForm({...editUserForm, role: value})}
                    >
                      <SelectTrigger id="edit-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="worker">Worker</SelectItem>
                        <SelectItem value="service_worker">Service Truck</SelectItem>
                        <SelectItem value="supervisor">Supervisor</SelectItem>
                        <SelectItem value="superintendent">Superintendent</SelectItem>
                        <SelectItem value="company_admin">Company Admin</SelectItem>
                        {user.role === 'super_admin' && (
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-isActive"
                    checked={editUserForm.isActive}
                    onChange={(e) => setEditUserForm({...editUserForm, isActive: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="edit-isActive">{t('adminUI.AccountIsActive')}</Label>
                </div>
              </div>
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditUserDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700"
                >{t('adminUI.SaveChanges')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Create User Dialog */}
        <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.CreateNewUser')}</DialogTitle>
              <DialogDescription>{t('adminUI.AddANewUserToYourCompany')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-name">{t('adminUI.FullName')}</Label>
                    <Input
                      id="create-name"
                      value={createUserForm.name}
                      onChange={(e) => setCreateUserForm({...createUserForm, name: e.target.value})}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-email">{t('adminUI.Email')}</Label>
                    <Input
                      id="create-email"
                      type="email"
                      value={createUserForm.email}
                      onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-password">{t('adminUI.Password')}</Label>
                    <Input
                      id="create-password"
                      type="password"
                      value={createUserForm.password}
                      onChange={(e) => setCreateUserForm({...createUserForm, password: e.target.value})}
                      placeholder="Minimum 6 characters"
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-phone">Phone (optional)</Label>
                    <Input
                      id="create-phone"
                      type="tel"
                      value={createUserForm.phone}
                      onChange={(e) => setCreateUserForm({...createUserForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="create-role">{t('adminUI.Role')}</Label>
                  <Select
                    value={createUserForm.role}
                    onValueChange={(value) => setCreateUserForm({...createUserForm, role: value, craft: value === 'worker' ? createUserForm.craft : ''})}
                  >
                    <SelectTrigger id="create-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="worker">Worker</SelectItem>
                      <SelectItem value="learner">Learner</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="company_admin">Company Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">{t('adminUI.SelectTheAppropriateRoleForThisUser')}</p>
                </div>
                
                {/* Craft Selection - Only for workers */}
                {createUserForm.role === 'worker' && (
                  <div className="space-y-2">
                    <Label htmlFor="create-craft">{t('adminUI.CraftTrade')}</Label>
                    <Select
                      value={createUserForm.craft}
                      onValueChange={(value) => setCreateUserForm({...createUserForm, craft: value})}
                    >
                      <SelectTrigger id="create-craft">
                        <SelectValue placeholder="Select worker's craft" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {crafts.length === 0 ? (
                          <div className="p-4 text-center text-sm text-slate-500">
                            No crafts available
                          </div>
                        ) : (
                          crafts
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((craft) => (
                              <SelectItem key={craft.id} value={craft.name}>
                                {craft.name}
                              </SelectItem>
                            ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">{t('adminUI.WorkerSTrainingCoursesWillBeCustomizedBasedOnTheirCraft')}</p>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateUserDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create User
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* JSA View Dialog */}
        <Dialog open={showJsaDialog} onOpenChange={setShowJsaDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('adminUI.JobSafetyAnalysisDetails')}</DialogTitle>
              <DialogDescription>{t('adminUI.ReviewCompletedAndSignedJsa')}</DialogDescription>
            </DialogHeader>

            {selectedJsa && (
              <div className="space-y-4 py-4">
                {/* Status Badge */}
                <div className="flex items-center gap-3">
                  <Badge className="bg-green-600 text-lg">{t('adminUI.Submitted')}</Badge>
                  <Badge variant="outline" className="text-lg">
                    {Object.keys(selectedJsa.workerAcknowledgments || {}).length} Worker Signatures
                  </Badge>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4 border-b pb-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('adminUI.TaskDescription')}</p>
                    <p className="font-semibold">{selectedJsa.taskDescription}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('adminUI.Supervisor')}</p>
                    <p className="font-semibold">{selectedJsa.supervisorName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('adminUI.Location')}</p>
                    <p className="font-semibold">{selectedJsa.location || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="font-semibold">{new Date(selectedJsa.date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('adminUI.SubmittedAt')}</p>
                    <p className="font-semibold">
                      {selectedJsa.submittedAt ? new Date(selectedJsa.submittedAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('adminUI.AssignedWorkers')}</p>
                    <p className="font-semibold">{selectedJsa.assignedWorkerNames || 'N/A'}</p>
                  </div>
                </div>

                {/* Worker Signatures */}
                <div>
                  <h3 className="font-semibold mb-2 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                    Worker Acknowledgments
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(selectedJsa.workerAcknowledgments || {}).map(([workerId, ack]) => (
                      <div key={workerId} className="bg-green-50 p-3 rounded border border-green-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{ack.signature}</p>
                            <p className="text-sm text-gray-600">
                              Signed: {new Date(ack.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hazards and Mitigations */}
                <div>
                  <h3 className="font-semibold mb-2 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                    Hazards & Mitigations ({selectedJsa.hazards?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {selectedJsa.hazards?.map((hazard, idx) => (
                      <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-start gap-2">
                          <div className="bg-orange-100 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 mb-1">
                              <span className="text-orange-600">{t('adminUI.Hazard')}</span> {hazard.hazard}
                            </p>
                            <p className="text-sm text-gray-700">
                              <span className="font-medium text-green-600">{t('adminUI.Mitigation')}</span> {hazard.mitigation}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                {selectedJsa.notes && (
                  <div>
                    <h3 className="font-semibold mb-2">{t('adminUI.AdditionalNotes')}</h3>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedJsa.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    onClick={() => {
                      console.log('=== DOWNLOAD BUTTON CLICKED ===');
                      console.log('Selected JSA ID:', selectedJsa?.id);
                      console.log('Selected JSA Task:', selectedJsa?.taskDescription);
                      console.log('Selected JSA Object:', selectedJsa);
                      handleDownloadJsaPdf(selectedJsa.id, selectedJsa.taskDescription);
                    }}
                    className="flex-1"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button
                    onClick={() => setShowJsaDialog(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminDashboard;
